import type { LocaleResources } from '../types';

export const es = {
  common: {
    matrixEditLabel: 'Cambiar nombre mostrado',
    matrixRestoreLabel: 'Restaurar nombre del catálogo',
    matrixPersonalLabelHint:
      'Solo para tu cuenta, compartido entre Ventas y Envíos. No cambia el catálogo.',
    matrixLabelSaveFailed: 'No se guardó el nombre. Actualiza los datos e inténtalo de nuevo.',
    matrixDisplayLabel: 'Tu nombre del producto',
    matrixLabelLimit: 'De 1 a 80 caracteres, texto sin formato.',
    matrixLabelPreview: 'Vista previa',
    appName: 'Operaciones KJÖTKOMPANIÐ',
    loading: 'Cargando…',
    readFromImage: 'Leer desde imagen',
    importOrderFile: 'Importar archivo de pedido',
    fileImportReview: 'Revisión de importación de archivo',
    pdfTextUnavailable: 'Este PDF no contiene texto para la importación automática.',
    storeMatches: 'TIENDA COINCIDE',
    possibleStoreMismatch: 'POSIBLE DISCREPANCIA DE TIENDA: se detectó {{store}}.',
    selectedStore: 'Tienda seleccionada: {{store}}',
    imageReview: 'Revisión de lectura de imagen',
    imageReadFailed: 'No se pudo leer la imagen. Puede introducir el pedido manualmente.',
    fileImportFailed: 'No se pudo leer el archivo. Puede introducir el pedido manualmente.',
    reviewRequired: 'Requiere revisión',
    unit: 'Unidad',
    addToDraft: 'Añadir al borrador del pedido',
    sources: 'Fuentes',
    save: 'Guardar',
    cancel: 'Cancelar',
    language: 'Idioma',
    appearance: 'Apariencia',
    appearanceLight: 'Claro',
    appearanceDark: 'Oscuro',
    appearanceSystem: 'Sistema',
    signedInAs: 'Sesión iniciada como {{name}}',
    foundationReady: 'La base de la plataforma está lista',
    foundationDescription: 'Los módulos operativos se entregarán en etapas posteriores aprobadas.',
    unavailable: 'Aún no disponible',
    quantityUnit: { PIECE: 'uds.', KG: 'kg' },
    errors: {
      MASTER_DATA_LOCK_TIMEOUT:
        'Otra operación está actualizando los datos maestros. Actualiza los datos e inténtalo de nuevo.',
      technicalId: 'ID técnico: {{id}}',
    },
    incompleteQuantity: 'Faltan algunos días de informes; el valor incluye solo días confirmados.',
    fractionalPieceQuantity: 'Una cantidad fraccionaria de unidades requiere revisión.',
  },
  auth: {
    title: 'Inicio de sesión seguro',
    email: 'Correo electrónico',
    code: 'Código de un solo uso',
    requestCode: 'Enviar código',
    verifyCode: 'Verificar e iniciar sesión',
    signOut: 'Cerrar sesión',
    sent: 'Si la cuenta está activa, se ha enviado un código de un solo uso.',
    codeHint: 'El código caduca en aproximadamente 10 minutos.',
    invalid: 'No se pudo verificar el código.',
    sessionExpired: 'La sesión ha caducado. Inicia sesión de nuevo.',
  },
  navigation: {
    open: 'Abrir navegación',
    close: 'Cerrar navegación',
    overview: 'Datos maestros',
    products: 'Productos',
    abpCatalog: 'Catálogo ABP',
    materials: 'Materiales e ingredientes',
    stores: 'Tiendas',
    networks: 'Cadenas',
    users: 'Usuarios',
    settings: 'Configuración',
    stock: 'Estado de existencias',
    sales: 'Ventas',
    shipments: 'Envíos',
    warehouse: 'Almacén',
    production: 'Producción',
    forecast: 'Pronóstico',
    costing: 'Calculadora de costes',
    productCostLibrary: 'Coste de producción de productos',
    costInputBase: 'Base de costes',
    profitability: 'Rentabilidad',
    orders: 'Pedidos',
    analytics: 'Analítica',
    dashboard: 'Panel',
    operations: 'Operaciones',
    importCenter: 'Centro de importación',
    overviewGroup: 'RESUMEN',
    salesGroup: 'VENTAS',
    operationsGroup: 'OPERACIONES',
    economicsGroup: 'ECONOMÍA',
    masterDataGroup: 'DATOS MAESTROS',
    collapseSidebar: 'Contraer barra lateral',
    expandSidebar: 'Expandir barra lateral',
  },
  users: {
    title: 'Usuarios y acceso',
    role: 'Rol',
    status: 'Estado',
    active: 'Activo',
    deactivated: 'Desactivado',
    managedByAdmin: 'La administración de usuarios está protegida por permisos del backend.',
    createUser: 'Añadir usuario',
    create: 'Crear usuario',
    cancel: 'Cancelar',
    close: 'Cerrar',
    email: 'Correo electrónico',
    displayName: 'Nombre visible',
    access: 'Excepciones de acceso',
    actions: 'Acciones',
    manage: 'Administrar',
    manageUser: 'Administrar {{email}}',
    overrideCount_one: '{{count}} excepción',
    overrideCount_other: '{{count}} excepciones',
    actionFailed: 'No se pudo guardar el cambio de acceso. Inténtalo de nuevo.',
    activate: 'Activar',
    deactivate: 'Desactivar',
    confirmActivate: '¿Activar este usuario?',
    confirmDeactivate: '¿Desactivar el usuario y revocar las sesiones activas?',
    revokeSessions: 'Revocar todas las sesiones',
    effectiveAccess: 'Acceso efectivo',
    effectiveAccessDescription:
      'Se muestran los valores del rol y cualquier excepción del usuario.',
    permission: 'Permiso',
    roleDefault: 'Valor del rol',
    override: 'Excepción',
    effective: 'Efectivo',
    inherit: 'Heredar rol',
    allow: 'Permitir',
    deny: 'Denegar',
    roles: {
      ADMIN: 'Administrador',
      ELDHUS_MANAGER: 'Responsable de Eldhús',
      SALESMAN: 'Vendedor',
      VIEWER: 'Lector',
    },
    roleDescriptions: {
      ADMIN: 'Acceso operativo completo; el rol y el estado están protegidos.',
      ELDHUS_MANAGER: 'Responsable operativo con acceso a almacén y datos maestros.',
      SALESMAN: 'Acceso a ventas y operaciones comerciales.',
      VIEWER: 'Acceso operativo de solo lectura.',
    },
  },
  settings: {
    title: 'Configuración personal',
    languageTitle: 'Idioma de la interfaz',
    languageDescription: 'Esta preferencia se guarda de forma segura en tu cuenta.',
    appearanceDescription: 'Elige una apariencia clara, oscura o la del sistema operativo.',
    saved: 'Preferencia de idioma guardada.',
  },
  validation: {
    required: 'Este campo es obligatorio.',
    invalidEmail: 'Introduce un correo electrónico válido.',
    sixDigitCode: 'Introduce el código de seis dígitos.',
    generic: 'Revisa los valores introducidos.',
  },
  master: {
    resultCount: '{{count}} resultados',
    previous: 'Anterior',
    next: 'Siguiente',
    viewSaved: 'Vista guardada',
    saveView: 'Guardar vista',
    search: 'Buscar',
    status: 'Estado',
    showAll: 'Mostrar todo',
    sort: 'Ordenar',
    updated: 'Actualizado',
    notConfigured: 'Sin configurar',
    ready: 'Los datos maestros están listos',
    readyDescription:
      'Gestiona la configuración canónica de productos, tiendas y comercio que usarán las etapas operativas posteriores.',
    productsCard: 'Productos',
    productsCardDescription:
      'Catálogo, unidades, mapeos, distribución e historial comercial efectivo.',
    storesCard: 'Tiendas',
    storesCardDescription:
      'Cadenas, ubicaciones, contactos, comentarios, mapeos y activación de productos.',
  },
  products: {
    title: 'Productos',
    description: 'Catálogo canónico de productos y configuración comercial efectiva.',
    create: 'Crear producto',
    sku: 'SKU',
    officialName: 'Nombre oficial',
    displayAlias: 'Alias visible',
    category: 'Categoría',
    salesUnit: 'Unidad de venta',
    financialUnit: 'Unidad financiera',
    operationalArea: 'Área operativa',
    netWeightKg: 'Peso neto (kg)',
    piecesPerTray: 'Piezas por bandeja',
    name: 'Nombre',
    units: 'Unidades',
    distribution: 'Distribución por cadena',
    salePriceNet: 'Precio de venta NETO',
    productionCostNet: 'Coste de producción NETO',
    back: 'Volver a productos',
    general: 'General',
    unitsSummary: 'Venta: {{sales}} · Finanzas: {{financial}} · Área: {{area}}',
    conversions: 'Conversiones de unidades del producto',
    fromUnit: 'Desde unidad',
    toUnit: 'A unidad',
    factor: 'Factor',
    effectiveFrom: 'Vigente desde',
    add: 'Añadir',
    network: 'Cadena',
    mode: 'Modo',
    directStore: 'Reposición directa de tienda',
    centralSellThrough: 'Venta a través de almacén central',
    addVersion: 'Añadir versión',
    centralExplanation:
      'La venta central no implica inventario de tienda, GOAL, reposición ni alertas. El registro de la línea base se aplaza a la etapa de inventario.',
    storeActivation: 'Activación por tienda',
    store: 'Tienda',
    stores: 'Tiendas',
    salePriceHistory: 'Historial de precio de venta habitual',
    netPrice: 'Precio unitario neto ISK',
    costHistory: 'Historial de coste de producción',
    netCost: 'Coste unitario neto ISK',
    planningFoundation: 'Base de GOAL, reposición y umbrales de alerta',
    notCalculated:
      'En esta etapa no existen valores calculados. Solo se guardan versiones manuales explícitas para combinaciones de distribución directa.',
    manualValue: 'Valor manual',
    setGoal: 'Definir GOAL',
    setReorder: 'Definir reposición manual',
    setThreshold: 'Definir umbral manual',
    aliases: 'Alias de origen',
    source: 'Origen',
    sourceSku: 'SKU de origen',
    sourceName: 'Nombre de origen',
    provenance: 'Procedencia del mapeo',
    audit: 'Historial de auditoría',
    categoryCode: 'Código de categoría',
    categoryName: 'Nombre de categoría',
    manageCategories: 'Gestionar categorías',
    createCategory: 'Crear categoría',
    safetyBuffer: 'Margen de seguridad',
    activate: 'Activar',
    deactivate: 'Desactivar',
    shipmentSourceRoute: 'Origen del envío',
    noShipmentSourceRoute: 'Sin asignación',
    routeReason: 'Motivo del cambio de ruta',
    saveShipmentSourceRoute: 'Guardar ruta de origen',
    shipmentSourceMatch: 'Coincidencia de SKU de envío',
    shipmentSourceMatchHelp:
      'Una coincidencia manual crea un alias autoritativo para futuras importaciones de envíos.',
    matchShipmentSource: 'Vincular producto existente',
    shipmentProductIntakeCount: 'Nuevos SKU de envíos que requieren revisión: {{count}}',
    shipmentProductIntakeTitle: 'Entrada de productos de envíos',
    shipmentProductIntakeReview: 'Requiere revisión',
    shipmentProductIntakeSku: 'SKU de origen',
    shipmentProductIntakeName: 'Producto de origen',
    shipmentProductIntakeStatus: 'Estado',
    shipmentProductIntakeSuggestion: 'Coincidencia / origen sugerido',
    shipmentProductIntakeStatus_NEW: 'Nuevo',
    shipmentProductIntakeStatus_MATCH_REVIEW_REQUIRED: 'Requiere revisar coincidencia',
    shipmentProductIntakeStatus_UNASSIGNED: 'Requiere decisión de origen',
    shipmentProductIntakeStatus_AUTO_CREATED: 'Creado automáticamente',
    shipmentProductIntakeStatus_RESOLVED: 'Resuelto',
  },
  stores: {
    title: 'Tiendas',
    description: 'Tiendas canónicas, contactos, mapeos de origen y activación de productos.',
    create: 'Crear tienda',
    network: 'Cadena',
    name: 'Nombre de tienda',
    shortName: 'Nombre corto',
    noPrimaryContact: 'Sin contacto principal',
    activeProductsCount: '{{count}} productos activos',
    primaryContact: 'Contacto principal',
    activeProducts: 'Productos activos',
    aliases: 'Alias de origen',
    back: 'Volver a tiendas',
    general: 'General',
    contacts: 'Contactos',
    primary: 'Principal',
    deactivate: 'Desactivar',
    activate: 'Activar',
    contactName: 'Nombre del contacto',
    roleTitle: 'Cargo / función',
    phone: 'Teléfono',
    email: 'Correo electrónico',
    setPrimary: 'Definir como principal',
    addContact: 'Añadir contacto',
    feedback: 'Comentarios operativos',
    feedbackText: 'Comentario',
    optionalProduct: 'Producto opcional',
    categoryTag: 'Categoría / etiqueta',
    addFeedback: 'Añadir comentario',
    product: 'Producto',
    source: 'Origen',
    rawAlias: 'Alias / código original',
    provenance: 'Procedencia del mapeo',
    addAlias: 'Añadir alias',
    extendHistory: 'Ampliar historial',
    audit: 'Historial de auditoría',
    editContact: 'Editar',
    updateContact: 'Actualizar contacto',
  },
  networks: {
    title: 'Cadenas',
    description: 'Cadenas comerciales canónicas compartidas por todos los usuarios.',
    create: 'Crear cadena',
    code: 'Código',
    name: 'Nombre canónico',
    shortName: 'Nombre corto',
    edit: 'Editar cadena',
  },
  inventory: {
    title: 'Inventario',
    warehouseTitle: 'Inventario de almacén',
    description:
      'Stock físico auditado con estado desconocido explícito e historial reconstruible.',
    positions: 'Posiciones de inventario',
    openingStock: 'Stock inicial',
    physicalCount: 'Conteo físico',
    physicalCountHelp:
      'Registra el stock contado inmutable y su diferencia frente a la cantidad observada en el libro.',
    warehouseCorrection: 'Corrección controlada',
    warehouseCorrectionHelp:
      'Registra una corrección de almacén auditada por separado; nunca edita un saldo directamente.',
    countHistory: 'Historial de conteos y diferencias',
    warehouseTransfer: 'Transferencia de almacén',
    warehouseTransferHelp:
      'Una transferencia es un movimiento inmutable entre dos posiciones de almacén ya inicializadas.',
    transferDestination: 'Almacén de destino',
    transferQuantity: 'Cantidad a transferir',
    submitTransfer: 'Registrar transferencia',
    transferPosted: 'Transferencia registrada. Origen: {{from}}, destino: {{to}}.',
    history: 'Historial de inventario',
    pipeline: 'Pipeline de red',
    locationKind: 'Tipo de ubicación',
    store: 'Tienda',
    warehouse: 'Almacén',
    location: 'Ubicación',
    allLocations: 'Todas las ubicaciones',
    product: 'Producto',
    unitFilter: 'Unidad',
    allUnits: 'Todas las unidades',
    current: 'Cantidad actual',
    actual: 'Cantidad física real',
    quality: 'Calidad de datos',
    stockStateLabel: 'Estado de stock',
    revision: 'Revisión',
    unknown: 'DESCONOCIDO',
    enterActual: 'Introducir conteo físico',
    physicalEntry: 'Conteo físico y stock inicial',
    businessDate: 'Fecha operativa',
    reason: 'Motivo',
    addOpeningLine: 'Añadir al borrador inicial',
    confirmOpening: 'Confirmar stock inicial',
    submitCorrection: 'Enviar corrección',
    submitPhysicalCount: 'Confirmar conteo físico',
    warehouseCountPosted: '{{count}} línea(s) de conteo físico registrada(s).',
    openingDraft: 'Borrador de stock inicial',
    date: 'Fecha',
    movement: 'Movimiento',
    result: 'Resultado',
    variance: 'Diferencia',
    varianceHelp: 'Cantidad física real menos la cantidad observada en el libro.',
    calculated: 'Cantidad calculada',
    lastMovement: 'Último movimiento',
    lastPhysicalCount: 'Último conteo físico',
    actions: 'Acciones',
    countAction: 'Contar',
    correctAction: 'Corregir',
    viewMovements: 'Movimientos',
    evidence: 'Evidencia',
    pieceUnit: 'uds.',
    kilogramUnit: 'kg',
    estimatedPipeline: 'PIPELINE DE RED ESTIMADO',
    pipelineExplanation:
      'Es una línea base verificada, no un cálculo en vivo del stock de tiendas.',
    pipelineRows: '{{count}} líneas base de pipeline',
    correctionPosted: 'Corrección registrada. Delta del libro: {{delta}}.',
    openingConfirmed: '{{count}} movimientos de stock inicial confirmados.',
    stockChanged:
      'El stock cambió durante la edición. Revise la cantidad actualizada y vuelva a enviar.',
    operationFailed: 'No se pudo completar la operación de inventario.',
    stockWorkspaceTitle: 'Estado de stock',
    stockWorkspaceDescription:
      'Vista operativa de solo lectura desde el libro mayor inmutable de inventario.',
    asOf: 'A fecha de',
    productSearch: 'Buscar producto',
    matrixOrientation: 'Orientación de matriz',
    storesByProducts: 'Tiendas × productos',
    productsByStores: 'Productos × tiendas',
    resetOrder: 'Restablecer orden',
    storesCount: '{{count}} tiendas',
    productsCount: '{{count}} productos',
    knownCells: '{{count}} conocidos',
    incompleteCells: '{{count}} incompletos',
    notInitializedCells: '{{count}} sin inicializar',
    notManagedCells: '{{count}} centrales',
    lowCells: '{{count}} bajos',
    workspaceLoadFailed: 'No se pudo cargar el estado de stock.',
    stockMatrix: 'Matriz de stock',
    rowLabel: 'Tienda / producto',
    notManaged: 'CENTRAL',
    notInitialized: 'DESCONOCIDO',
    notApplicable: 'Producto no activo para esta tienda',
    stockDrillDown: 'Detalle de stock',
    goal: 'OBJETIVO',
    difference: 'Diferencia',
    reorder: 'Reposición',
    provisional: 'provisional',
    distributionMode: 'Modo de distribución',
    ledgerExplanation: 'Explicación del libro mayor',
    stockState: {
      KNOWN: 'CONOCIDO',
      NOT_INITIALIZED: 'SIN INICIALIZAR',
      INCOMPLETE_DATA: 'DATOS INCOMPLETOS',
      NOT_MANAGED: 'GESTIONADO CENTRALMENTE',
      NOT_APPLICABLE: 'NO ACTIVO PARA ESTA TIENDA',
    },
  },
  sales: {
    title: 'Espacio de ventas',
    description: 'Vista operativa de solo lectura de informes diarios de ventas confirmados.',
    datePreset: 'Rango de fechas',
    from: 'Desde',
    to: 'Hasta',
    network: 'Red',
    allNetworks: 'Todas las redes',
    store: 'Tienda',
    allStores: 'Todas las tiendas',
    productSearch: 'Buscar producto',
    product: 'Producto',
    allProducts: 'Todos los productos',
    orientation: 'Orientación de matriz',
    storesByProducts: 'Tiendas × productos',
    productsByStores: 'Productos × tiendas',
    knownSales: 'Ventas conocidas',
    storesCount: '{{count}} tiendas',
    productsCount: '{{count}} productos activos',
    reportDays: '{{confirmed}} / {{expected}} días de informe confirmados',
    matrix: 'Matriz de ventas',
    rowLabel: 'Tienda / producto',
    drillDown: 'Detalle de celda',
    date: 'Fecha',
    quantity: 'Cantidad',
    coverageLabel: 'Cobertura',
    known: 'Conocido',
    knownZero: 'Cero conocido',
    unknown: 'DESCONOCIDO',
    unknownCell: 'Sin datos ⚠',
    sum: 'TOTAL',
    totalDrillDownHint: 'Mostrar dónde y cuándo se vendió',
    openTotalDrillDown: 'Mostrar total de {{column}}',
    productSalesInPeriod: 'VENTAS DEL PRODUCTO EN EL PERIODO',
    storeSalesInPeriod: 'VENTAS DE LA TIENDA EN EL PERIODO',
    totalDetails: 'DETALLES',
    byStore: 'POR TIENDA',
    byDay: 'POR DÍA',
    showZeros: 'Mostrar ceros',
    hideZeros: 'Ocultar ceros',
    share: 'Participación',
    storesWithSales: 'Tiendas con ventas',
    incompleteTotals: 'DATOS INCOMPLETOS',
    incompleteTotalsDescription:
      'El ranking y el total incluyen solo cantidades confirmadas conocidas.',
    loadFailed: 'No se pudo cargar el espacio de ventas.',
    coverage: { COMPLETE: 'COMPLETO', INCOMPLETE_DATA: 'DATOS INCOMPLETOS' },
    coverageDetailOpen: 'Abrir estado de cobertura de ventas',
    coverageDetailTitle: 'Estado de cobertura de datos',
    coverageDetailGoToImportCenter: 'Ir al Centro de importación',
    coverageDetailGroup: {
      missing: 'PENDIENTE',
      ready: 'LISTO PARA CONFIRMAR',
      needsFixing: 'REQUIERE REVISIÓN',
    },
    coverageDetailState: {
      NOT_UPLOADED: 'Sin informe',
      PROCESSING: 'En proceso',
      PREVIEW_READY: 'Listo para confirmar',
      BLOCKED: 'Bloqueado',
      CONFLICT: 'Conflicto',
      FAILED: 'Error de importación',
    },
    importConflictsOpen: 'Abrir conflictos de importación',
    importConflictsCount: 'CONFLICTOS DE IMPORTACIÓN: {{count}}',
    importConflictsTitle: 'CONFLICTOS DE IMPORTACIÓN',
    importConflictExistingConfirmed: 'Informe existente confirmado',
    compareConflict: 'Comparar',
    importConflictAuthoritativeHint:
      'El informe confirmado sigue siendo autoritativo hasta una decisión explícita.',
    quickPeriods: 'Periodos rápidos',
    weekPeriods: 'Semanas',
    isoYear: 'Año',
    isoWeek: 'Semana',
    isoWeekOption: 'Semana {{week}} · {{range}}',
    notDue: 'Aún no requerido',
    activeRange: '{{from}} – {{to}}',
    expandFilters: 'Mostrar filtros',
    collapseFilters: 'Ocultar filtros',
    columns: 'Columnas',
    searchColumns: 'Buscar columnas',
    showAllColumns: 'Mostrar todas',
    hideAllColumns: 'Ocultar todas',
    allMatrixColumns: 'Todas las columnas',
    visibleColumnsCount: 'Visibles: {{visible}} / {{total}}',
    columnSearchResults: 'Resultados de búsqueda',
    showSearchResults: 'Mostrar resultados',
    hideSearchResults: 'Ocultar resultados',
    noColumnSearchResults: 'No hay columnas coincidentes',
    noProductsSelected: 'No se seleccionaron productos',
    noStoresSelected: 'No se seleccionaron tiendas',
    pinColumn: 'Fijar al inicio',
    resetColumnLayout: 'Restablecer diseño',
    resetRowOrder: 'Restablecer orden de filas',
    pieceAbbreviation: 'uds.',
    kgAbbreviation: 'kg',
    preset: {
      '7d': '7 días',
      '28d': '28 días',
      '90d': '90 días',
      ytd: 'AÑO',
      last_confirmed_day: 'Último día',
      last_2_confirmed_days: 'Últimos 2 días',
      last_3_confirmed_days: 'Últimos 3 días',
      last_7_calendar_days: 'Últimos 7 días',
      last_completed_weekend: 'Último fin de semana',
      current_week: 'Esta semana',
      previous_week: 'Semana anterior',
      iso_week: 'Elegir semana',
      custom: 'Intervalo personalizado',
    },
  },
  salesImports: {
    title: 'Informes de ventas',
    description: 'Cargue, valide, confirme y revierta libros diarios oficiales de Sales Pivot.',
    upload: 'Cargar libro',
    history: 'Historial de importación',
    filename: 'Archivo',
    salesDate: 'Fecha de venta',
    status: 'Estado',
    statusValue: {
      UPLOADED: 'Cargado',
      PROCESSING: 'En proceso',
      PREVIEW_READY: 'Listo para confirmar',
      BLOCKED: 'Bloqueado',
      DUPLICATE: 'Duplicado',
      DATE_CONFLICT: 'Conflicto de datos',
      FAILED: 'Error de importación',
      CONFIRMED: 'Confirmado',
      CANCELLED: 'Cancelado',
      ROLLED_BACK: 'Revertido',
    },
    uploaded: 'Cargado',
    stores: 'Tiendas',
    products: 'Productos',
    nonzero: 'Hechos distintos de cero',
    total: 'Cantidad total',
    mappings: 'Mapeo y validación',
    issues: 'Avisos de validación',
    canonicalIdentityConflict:
      'Varias identidades de origen apuntan a un Producto canónico, pero no se pueden agregar de forma segura.',
    canonicalizedProductsNotice:
      'Varias identidades de origen verificadas se han agregado al Producto canónico. Cada celda de origen sigue disponible para auditoría.',
    canonicalizedSourceCells: 'Celdas de origen agregadas',
    sourceCell: 'Celda de origen',
    sourceQuantity: 'Cantidad de origen',
    nonRetailWarehouseDestination: 'Excluido: destino de almacén no minorista',
    storeIdentityMissing: 'Bloqueado: falta la identidad de la tienda',
    distributionPolicyMissing: 'Falta la política de distribución del Producto {{sku}} {{product}}',
    fixDistributionPolicy: 'Corregir política',
    resolveStore: 'Añadir o vincular tienda',
    issueOccurrences: '{{count}} apariciones',
    sample: 'Muestra analizada',
    confirm: 'Confirmar informe oficial',
    revalidate: 'Revalidar mapeos',
    retry: 'Reintentar desde la fuente guardada',
    rollback: 'Revertir informe',
    rollbackReason: 'Motivo obligatorio de reversión',
    detail: 'Detalle del informe',
    duplicate: 'Duplicado: estos datos ya están confirmados.',
    showExistingReport: 'Mostrar informe existente',
    conflict: 'La nueva fuente contiene datos distintos al informe confirmado para esta fecha.',
    conflictComparisonTitle: 'Comparación de conflicto de importación',
    conflictComparisonDescription:
      'Compare el informe confirmado con la nueva fuente. Cambiar los datos requiere una decisión explícita.',
    existingConfirmedReport: 'INFORME ACTUAL — CONFIRMADO',
    conflictingEvidence: 'NUEVA FUENTE — CONFLICTO',
    conflictScope: 'Tiendas: {{stores}} · Productos: {{products}}',
    conflictDifferences: 'Posiciones Tienda × Producto modificadas',
    store: 'Tienda',
    product: 'Producto',
    existingValue: 'Actual',
    newValue: 'Nuevo valor',
    difference: 'Diferencia',
    keepExistingReport: 'Conservar informe actual',
    replaceWithNewReport: 'Reemplazar con el nuevo informe',
    replaceConfirm:
      '¿Reemplazar el informe de ventas confirmado por la nueva versión? El informe anterior se conservará como evidencia histórica y este día se recalculará desde el nuevo informe.',
    replaceBack: 'Volver',
    keepConfirm:
      'Mantener el informe confirmado actual como verdad autorizada de Ventas. El archivo nuevo permanece como evidencia inmutable del conflicto.',
    conflictReason: 'Motivo de la decisión',
    conflictReasonHelp:
      'Obligatorio para la auditoría. Explica por qué se mantiene o sustituye el informe.',
    conflictAdminReason:
      'Solo un Administrador puede tomar esta decisión auditada. El motivo es obligatorio.',
    confirmConflictDecision: 'Confirmar decisión auditada',
    conflictStepExplain: 'Conflicto',
    conflictStepCompare: 'Comparar',
    conflictStepDecision: 'Decisión',
    conflictStepConfirm: 'Confirmación',
    conflictRows_one: '{{count}} fila distinta de cero',
    conflictRows_other: '{{count}} filas distintas de cero',
    changeType: 'Cambio',
    changeTypeValue: { ADDED: 'Añadido', REMOVED: 'Eliminado', CHANGED: 'Cambiado' },
    conflictHelpTitle: '¿Cómo resuelvo un conflicto?',
    conflictHelpBody:
      'Compara fuente, usuario, hora, alcance, filas y cada valor Tienda × Producto modificado. Mantén el informe actual si sigue siendo autoritativo. Sustitúyelo solo cuando la nueva fuente sea el informe oficial corregido; el anterior permanece en el historial.',
    conflictResolution: {
      KEEP_EXISTING: 'Conflicto revisado. El informe confirmado actual sigue siendo autoritativo.',
      REPLACED:
        'La nueva versión del informe está confirmada. La anterior permanece en el historial.',
    },
    completeZero:
      'Informe completo sin hecho significa cero conocido. Informe ausente significa desconocido.',
    choose: 'Seleccione un libro Sales Pivot .xlsx',
    back: 'Volver al historial',
    download: 'Descargar original',
    operationFailed: 'No se pudo completar la operación de importación de ventas.',
  },
  importCenter: {
    email: {
      source: 'Correo electrónico',
      sourceFilter: 'Origen',
      browserSource: 'Carga del navegador',
      title: 'Importación de adjuntos por correo',
      settings: 'Administración del correo',
      safety:
        'La recepción solo prepara y valida los adjuntos. La confirmación sigue siendo manual; no se crean automáticamente hechos de ventas, envíos, inventario ni flujo.',
      configured:
        'La configuración de la aplicación está completa. Verifique por separado el DNS y la recepción real antes de activar.',
      notConfigured:
        'La recepción de correo no está configurada. Las direcciones no están activas. Consulte la guía de activación de STAGING.',
      disabled: 'La recepción de correo está desactivada.',
      enabled: 'Activar importación de adjuntos por correo',
      addressMissing: 'Dirección sin configurar',
      addressActive: 'Activada en la configuración de la aplicación',
      addressInactive: 'Inactiva — no envíe informes aquí',
      copyAddress: 'Copiar dirección {{type}}',
      copied: 'Dirección copiada.',
      copyFailed: 'No se pudo copiar. Seleccione manualmente la dirección activa.',
      authenticationPolicy:
        'La importación automática requiere remitente y tipo permitidos y SPF, DKIM y DMARC pass de Resend. Solo jakub@kjotkompani.is puede usar la excepción aprobada DKIM gray con SPF y DMARC pass; DKIM fail o unknown nunca se acepta. Otros resultados inciertos quedan en cuarentena. La confirmación del informe sigue siendo manual.',
      autoReleaseEnabled: 'Importar automáticamente mensajes autenticados de remitentes permitidos',
      providerVerified:
        'Liberado automáticamente conforme a la política aprobada de autenticación del remitente con resultados de Resend. La confirmación del informe sigue siendo manual.',
      senders: 'Remitentes y tipos de informe permitidos',
      senderAddress: 'Correo del remitente',
      addSender: 'Añadir remitente',
      removeSender: 'Eliminar remitente',
      limits:
        'Límites: {{attachments}} adjuntos; {{fileBytes}} bytes por archivo; {{totalBytes}} bytes por mensaje.',
      reason: 'Motivo de auditoría',
      reasonHint: 'Al menos 10 caracteres. No incluya secretos ni contenido del mensaje.',
      actionFailed:
        'La acción no fue aceptada. Revise la configuración, permisos del remitente, estado actual y límite de intentos; después actualice.',
      settingsFailed: 'No se pudo cargar la configuración del correo.',
      cancel: 'Cancelar',
      save: 'Guardar ajustes',
      proceed: 'Continuar',
      refresh: 'Actualizar',
      empty: 'No hay fuentes de correo recibidas para este filtro.',
      typeUnresolved: 'Tipo de informe sin resolver',
      counts:
        'Adjuntos {{total}} · procesados {{processed}} · omitidos {{skipped}} · fallidos {{failed}} · pendientes {{pending}}',
      adminApproved: 'Un administrador revisó la evidencia del remitente y liberó el mensaje.',
      unverified: 'La autenticación del remitente no está verificada.',
      release: 'Revisar / liberar cuarentena',
      retry: 'Reintentar adjuntos sin terminar',
      attachments: 'Adjuntos y procedencia ({{total}})',
      identifiers: 'Correo del proveedor: {{provider}} · Message-ID: {{messageId}}',
      attachmentMeta: '{{size}} bytes · intentos {{attempts}}',
      unknown: 'Desconocido',
      pendingHash: 'Aún no almacenado',
      provenance:
        'Adjunto del proveedor: {{attachment}} · import_file: {{file}} · elemento del lote: {{item}}',
      reviewReport: 'Revisar validación / informe',
      releaseWarning:
        'Verifique al remitente de forma independiente antes de liberar. El remitente y el tipo deben seguir permitidos. Esto solo autoriza la preparación, nunca la confirmación.',
      retryWarning:
        'Solo se reintenta el trabajo pendiente o fallido, dentro del límite del servidor. No se duplican adjuntos completados ni evidencias comerciales.',
      statusRECEIVED: 'Recibido',
      statusPROCESSING: 'Procesando',
      statusQUARANTINED: 'En cuarentena',
      statusREADY: 'Listo para confirmación manual',
      statusBLOCKED: 'Validación bloqueada',
      statusDUPLICATE: 'Duplicado',
      statusCOMPLETED_WITH_ERRORS: 'Completado con errores',
      statusFAILED: 'Fallido',
      statusPENDING: 'Pendiente',
      statusSKIPPED: 'Omitido',
      statusCOMPLETED: 'Completado',
      statusCONFIRMED: 'Confirmado manualmente',
      statusCANCELLED: 'Cancelado',
      statusROLLED_BACK: 'Revertido',
      pageSummary: 'Mensajes {{from}}–{{to}} de {{total}}',
      previousPage: 'Mensajes anteriores',
      nextPage: 'Mensajes siguientes',
      reasonAuthentication:
        'Verifique al remitente de forma independiente y solicite la liberación al administrador.',
      reasonSender: 'El remitente o tipo no está permitido. Revise la lista del administrador.',
      reasonRecipient:
        'El destinatario no identifica un único tipo de informe. Use una sola dirección de recepción configurada.',
      reasonUnsupported: 'Se omitió un adjunto no compatible; los demás continúan.',
      reasonLimit: 'Se superó un límite de seguridad del archivo, mensaje o libro.',
      reasonWorkbook:
        'El adjunto no es un libro XLSX válido y compatible. Solicite una fuente corregida.',
      reasonUnsafe: 'Se rechazó contenido cifrado, ejecutable, con fórmulas o no seguro del libro.',
      reasonValidation:
        'El analizador existente rechazó esta fuente. Revise la validación antes de reintentar.',
      reasonTechnical:
        'El procesamiento requiere atención. Conserve la fuente y comunique este código si falla el reintento.',
    },
    title: 'Centro de importación',
    description: 'Bandeja operativa de informes de Ventas y Envíos.',
    loading: 'Cargando importaciones…',
    loadFailed: 'No se pudo cargar el Centro de importación.',
    dateUnknown: 'Fecha pendiente de validación',
    scopeUnknown: 'Alcance pendiente de validación',
    openBatch: 'Abrir lote',
    backToInbox: 'Volver a la bandeja',
    salesBatch: 'Lote de Ventas',
    shipmentBatch: 'Lote de Envíos',
    batchItems: 'Archivos del lote',
    businessDates_one: '{{count}} fecha de informe',
    businessDates_other: '{{count}} fechas de informe',
    confirmAllSafety: 'Solo se confirman informes válidos según las reglas existentes.',
    confirmResult:
      'Confirmados {{confirmed}}; ya confirmados {{already_confirmed}}; bloqueados {{blocked}}; errores {{failed}}.',
    tabs: {
      label: 'Secciones del Centro de importación',
      actions: 'Requiere acción',
      missing: 'Informes faltantes',
      history: 'Historial',
      upload: 'Carga masiva',
    },
    filters: {
      from: 'Desde',
      to: 'Hasta',
      type: 'Tipo',
      all: 'Todos',
      network: 'Red',
      store: 'Tienda',
      search: 'Archivo, fecha o identificador',
      filename: 'Nombre de archivo',
      reportDate: 'Fecha del informe',
      status: 'Estado',
      sort: 'Ordenar',
      ascending: 'Ascendente',
      descending: 'Descendente',
      showTestData: 'Mostrar datos de prueba',
    },
    columns: {
      report: 'Informe',
      source: 'Fuente',
      scope: 'Alcance',
      reason: 'Motivo',
      counts: 'Filas',
      quantity: 'Cantidad',
      action: 'Acción',
      date: 'Fecha',
      type: 'Tipo',
      deadline: 'Esperado para',
      state: 'Estado',
      uploader: 'Subido por',
      identifier: 'Identificador',
    },
    actions: {
      title: 'Informes que requieren acción',
      previousStatus: 'Informe anterior: {{status}}',
      rows: '{{count}} filas',
      valid: '{{count}} válidas',
      errors: '{{count}} errores',
      unrecognized: '{{count}} no reconocidas',
      emptyTitle: 'No se requiere acción',
      emptyBody: 'Ninguna importación requiere acción ahora.',
      retry: 'Reintentar',
      viewIssues: 'Ver problemas',
      preview: 'Vista previa / confirmar',
      openReport: 'Abrir informe',
      processing: 'Procesando',
      confirmAll: 'Confirmar todos los listos',
      recalculateBatchStatus: 'Recalcular estado del lote',
      revalidate: 'Revalidar',
      revalidateBackfill: 'Revalidar fechas',
    },
    statusReconciliation: {
      title: 'Recalcular estado del lote',
      loading: 'Comprobando el estado de dominio autoritativo…',
      safety:
        'Esta operación solo sincroniza el estado operativo del archivo con un informe ya confirmado. No ejecuta análisis, validación ni confirmación y no modifica informes ni hechos.',
      current: 'Actual: {{status}}',
      expected: 'Después de reparar: {{status}}',
      eligible: 'Para sincronizar: {{count}}',
      excluded: 'Fuera del alcance: {{count}}',
      currentState: 'Estado del archivo',
      authoritativeState: 'Estado del informe',
      scope: 'Alcance de la reparación',
      included: 'Incluido en la reparación',
      exclusionITEM_NOT_PREVIEW_READY: 'El estado del archivo no es apto',
      exclusionDOMAIN_BATCH_MISSING: 'No hay informe de dominio vinculado',
      exclusionDOMAIN_BATCH_NOT_CONFIRMED: 'El informe de dominio no está confirmado',
      noDrift: 'Este lote ya está sincronizado.',
      zeroEffects: 'Informes, hechos, eventos de confirmación, mapeos y políticas: 0 cambios.',
      reason: 'Motivo obligatorio',
      reasonHelp: 'El actor, la hora y el estado exacto anterior y posterior quedan auditados.',
      confirmation: 'Escribe {{value}}',
      cancel: 'Cancelar',
      apply: 'Recalcular estado',
      saving: 'Guardando…',
      previewFailed: 'No se pudo preparar la vista previa. Actualiza el lote e inténtalo de nuevo.',
      failed: 'El estado cambió o la sincronización falló. No se guardó nada.',
      success: 'Se sincronizaron {{count}} archivos. Estado del lote: {{status}}.',
    },
    conflicts: {
      title: 'Conflictos de importación',
      description: 'Compara las nuevas fuentes con los informes confirmados activos.',
      grouping: 'Agrupación de conflictos',
      groupByReport: 'Por informe',
      groupBySourceUpload: 'Por carga de origen',
      sourceCardLabel: 'Conflictos del archivo {{filename}}',
      sourceUploadedBy: 'Subido por: {{uploader}} · {{date}}',
      sourceDateRange: 'Rango de fechas: {{from}}–{{to}}',
      sourceConfirmed: 'Particiones confirmadas: {{count}} · hechos: {{facts}}',
      sourceConflicts: 'Conflictos por cerrar: {{count}}',
      sourceConflictBreakdown: 'Diferencias: {{material}} · bloqueados: {{blocked}}',
      sourceOtherBlocked: 'Otras particiones bloqueadas: {{count}}',
      sourceKeepEffect:
        'Conserva los informes oficiales. No cambia los datos confirmados ni las otras particiones bloqueadas de esta carga.',
      closeSourceConflicts:
        'Conservar los informes oficiales y cerrar {{count}} conflictos de esta carga',
      sourceEmptyTitle: 'No hay cargas con conflictos abiertos',
      sourceEmptyBody: 'Ninguna carga de origen contiene conflictos que deban cerrarse.',
      sourceKeepTitle: 'Conservar informes oficiales para la carga de origen',
      sourcePreservedEvidence:
        'El archivo, los datos de origen, las incidencias y los informes confirmados existentes se conservan.',
      sourceEvidenceDifferences:
        'Diferencias de la fuente que se cerrará: solo informativas; no se guardarán como hechos',
      sourceExpectedEffects:
        'Efecto de la operación: informes Sales {{reports}}, hechos Sales {{facts}}, confirmaciones {{confirmations}}, mapeos {{mappings}}, políticas {{policies}}, revalidaciones {{revalidations}}.',
      total: 'Total: {{count}}',
      materialCount: 'Diferencias comerciales: {{count}}',
      blockedCount: 'Bloqueados: {{count}}',
      classification: 'Clasificación',
      classificationEXACT_DUPLICATE: 'Duplicado exacto',
      classificationMETADATA_ONLY: 'Solo metadatos',
      classificationMATERIAL_DIFFERENCE: 'Diferencia comercial',
      classificationBLOCKED: 'Bloqueado',
      existingReport: 'Informe existente',
      newReport: 'Informe nuevo',
      changed: 'Cambiados',
      added: 'Añadidos',
      removed: 'Eliminados',
      negatives: 'Negativos',
      recommendation: 'Recomendación',
      recommendationKEEP_EXISTING: 'Conservar existente',
      recommendationCOMPARE: 'Comparar y decidir',
      recommendationRESOLVE_BLOCKERS: 'Resolver bloqueos primero',
      compare: 'Comparar',
      openFullComparison: 'Comparación completa',
      factCount: '{{count}} posiciones',
      selectPage: 'Seleccionar página actual',
      clearPage: 'Desmarcar página actual',
      selectAllResults: 'Seleccionar todos los resultados',
      clearAll: 'Desmarcar todo',
      selectedCount: 'Seleccionados: {{count}}',
      selectReport: 'Seleccionar informe del {{date}}',
      allFilteredSelected: 'Todos los resultados no bloqueados del filtro están seleccionados.',
      keepExisting: 'Conservar existentes',
      replaceWithNew: 'Reemplazar por nuevos',
      keepTitle: 'Conservar informes existentes',
      replaceTitle: 'Reemplazar informes activos por nuevos',
      previewCounts: 'Elegibles: {{eligible}} · excluidos: {{excluded}}',
      excludedReports: 'Informes excluidos y motivos',
      reason: 'Motivo común obligatorio',
      confirmationText: 'Escribe {{value}}',
      acceptNegatives: 'Acepto {{count}} correcciones negativas en los informes nuevos',
      cancel: 'Cancelar',
      execute: 'Ejecutar',
      executing: 'Ejecutando…',
      operationFailed: 'No se pudo preparar o ejecutar la operación. Actualiza la comparación.',
      retryIncomplete: 'Reintentar incompletos',
      partitionResults: 'Resultados de particiones ({{count}})',
      partitionResultsLabel: 'Resultados de la operación por partición de origen',
      partitionResult: 'Resultado',
      none: 'Ninguno',
      operationStatus:
        '{{status}} · completados {{completed}} · omitidos {{skipped}} · fallidos {{failed}}',
      operationStatusPENDING: 'Pendiente',
      operationStatusRUNNING: 'En curso',
      operationStatusPARTIAL: 'Completada parcialmente',
      operationStatusCOMPLETED: 'Completada',
      operationStatusSKIPPED: 'Omitida',
      operationStatusFAILED: 'Fallida',
      guardTEST_FIXTURE: 'Los datos de prueba están excluidos',
      guardNOT_MATERIAL_DIFFERENCE: 'El informe no contiene diferencias de negocio',
      guardUNIT_MISMATCH: 'Las unidades de los informes no coinciden',
      guardUNKNOWN_UNIT: 'El informe contiene una unidad no compatible',
      guardACTIVE_REPORT_COUNT_CHANGED: 'El número de informes activos ha cambiado',
      guardPARALLEL_CONFLICT: 'Existe más de un conflicto para esta fecha',
      guardSEMANTIC_HASH_MISSING: 'Falta la huella completa de datos del informe',
      guardSOURCE_EVIDENCE_MISSING: 'Falta el vínculo inmutable con la carga de origen',
      guardSOURCE_EVIDENCE_MISMATCH: 'La evidencia inmutable de la carga de origen ya no coincide',
      guardAUTHORITATIVE_REPORT_NOT_CONFIRMED:
        'El informe seleccionado como oficial ya no está confirmado',
      guardIMPORT_CONFLICT_STATE_CHANGED:
        'El estado del informe o de la evidencia de origen cambió después de la vista previa',
      guardIMPORT_CONFLICT_OPERATION_FAILED:
        'No se pudo cerrar esta partición; los elementos incompletos pueden reintentarse de forma segura',
      guardSHIPMENT_RECONCILIATION_REQUIRED: 'Debe completarse la conciliación del envío',
      guardSHIPMENT_PARTIAL_RANGE_REPLACE_UNSAFE:
        'Una fuente más corta no puede sustituir todo el informe de envíos confirmado',
      emptyTitle: 'Sin conflictos abiertos',
      emptyBody: 'Ningún informe requiere comparación con un informe confirmado.',
    },
    incompleteSource: {
      title: 'Rechazar particiones de origen incompletas',
      selectedCount: 'Particiones incompletas seleccionadas: {{count}}',
      selectEligible: 'Seleccionar elegibles en esta página',
      clearSelection: 'Borrar selección',
      previewAction: 'Vista previa del rechazo',
      preparing: 'Preparando vista previa…',
      previewFailed:
        'No se pudo preparar la vista previa. Actualice la bandeja e inténtelo de nuevo.',
      selectPartition: 'Seleccionar partición incompleta de {{filename}}',
      select: 'Seleccionar para rechazar',
      previewCounts:
        'Seleccionadas: {{selected}} · elegibles: {{eligible}} · excluidas: {{excluded}}',
      eligible: 'Elegible',
      excluded: 'Excluida',
      blockers: 'Evidencia bloqueante',
      unknownBlocker: 'Problema bloqueante sin resolver',
      quantityTotals: 'Cantidades de origen distintas de cero',
      entityCounts: 'Tiendas: {{stores}} · Productos: {{products}}',
      sourceRows: 'Celdas de origen distintas de cero: {{cells}} · filas {{first}}–{{last}}',
      blankStoreEvidence: 'Evidencia de Tienda en blanco: {{count}} columnas de origen sin asignar',
      currentEffects:
        'Efectos comerciales actuales — informes: {{reports}} · hechos: {{facts}} · informes confirmados activos: {{confirmed}}',
      sharedSource:
        'Origen compartido — particiones: {{partitions}} · confirmadas: {{confirmed}} · duplicados: {{duplicates}} · hechos confirmados: {{facts}}',
      exclusionReasons: 'No se puede rechazar',
      notEligible: 'Esta partición ya no es elegible',
      effect:
        'Particiones cambiadas a CANCELADAS: {{count}}. Informes de Ventas: {{reports}}; hechos de Ventas: {{facts}}. No habrá asignaciones, confirmaciones, revalidaciones, eliminaciones ni cambios en el archivo compartido. La cobertura de cada fecha seguirá como FALTANTE (desconocida), nunca cero.',
      reason: 'Motivo obligatorio del rechazo',
      reasonHelp: 'El motivo se conserva en la evidencia de auditoría inmutable.',
      confirmationText: 'Escriba exactamente: {{value}}',
      confirmationHelp: 'El texto debe coincidir exactamente con la vista previa del servidor.',
      reject: 'Rechazar origen incompleto',
      rejecting: 'Rechazando…',
      cancel: 'Cancelar',
      close: 'Cerrar',
      operationFailed:
        'No se pudo completar el rechazo del origen incompleto. Actualice la bandeja y prepare una nueva vista previa.',
      operationId: 'ID de operación: {{operationId}}',
      replayed: 'Repetición idempotente',
      result: '{{status}} · completadas {{completed}} · omitidas {{skipped}} · fallidas {{failed}}',
      resultsLabel: 'Resultados del rechazo del origen incompleto',
      resultStatusCOMPLETED: 'Completado',
      resultStatusPARTIAL: 'Completado parcialmente',
      resultStatusFAILED: 'Fallido',
      outcomeStatusCOMPLETED: 'Cancelada',
      outcomeStatusSKIPPED: 'Omitida',
      outcomeStatusFAILED: 'Fallida',
      none: 'Ninguno',
      partitionFailed: 'No se pudo rechazar esta partición',
      errorCodeIMPORT_INCOMPLETE_SOURCE_STATE_CHANGED:
        'La evidencia de origen cambió tras la vista previa',
      errorCodeIMPORT_INCOMPLETE_SOURCE_NOT_REJECTABLE: 'La partición ya no es elegible',
      errorCodeIMPORT_INCOMPLETE_SOURCE_PREVIEW_MISMATCH:
        'La vista previa ya no coincide con la solicitud',
      errorCodeIMPORT_INCOMPLETE_SOURCE_CONFIRMATION_MISMATCH:
        'El texto de confirmación no coincidió',
      guardIMPORT_TYPE_NOT_SUPPORTED: 'Solo se admiten particiones de Ventas',
      guardSTATUS_NOT_BLOCKED: 'La partición ya no está bloqueada',
      guardSOURCE_PARTITION_MISSING: 'Falta la evidencia inmutable de la partición de origen',
      guardALREADY_CONFIRMED: 'La partición ya fue confirmada',
      guardIS_DUPLICATE: 'La partición es un duplicado',
      guardIS_CONFLICT: 'La partición tiene un conflicto de importación',
      guardHAS_BUSINESS_EFFECT: 'La partición ya tiene un efecto comercial',
      guardDATE_ALREADY_CONFIRMED: 'La fecha ya tiene un informe confirmado',
      guardIS_DUPLICATE_CANONICAL: 'Otra importación usa esta partición como origen duplicado',
      guardHAS_CONFLICT_RESOLUTION: 'Ya existe una decisión de conflicto para esta partición',
      guardTEST_DATA_USES_SEPARATE_WORKFLOW: 'Los datos de prueba usan su proceso separado',
      guardNO_BLOCKING_ISSUE: 'No queda ningún problema bloqueante',
      guardNO_EMPTY_UNKNOWN_STORE_EVIDENCE:
        'No se encontró un bloqueo de Tienda desconocida en blanco',
      guardNO_EMPTY_UNMAPPED_STORE_EVIDENCE:
        'No se encontró evidencia de Tienda sin asignar en blanco',
      guardHAS_ADDITIONAL_BLOCKING_ISSUES:
        'La partición tiene bloqueos adicionales y requiere una decisión separada',
      guardHAS_NON_EMPTY_OR_MAPPED_STORE_EVIDENCE:
        'La partición contiene una tienda reconocida o no vacía y no puede rechazarse completa',
      guardNO_FRAGMENTARY_ROWS: 'No se encontraron filas fragmentarias de origen distintas de cero',
    },
    productIssues: {
      title: 'Problemas comunes',
      description:
        'Una decisión de asignación puede desbloquear de forma segura todos los informes no confirmados con el mismo código de origen.',
      sourceSku: 'Código de origen',
      sourceNames: 'Nombre de origen',
      affectedReports: 'Informes afectados',
      reportCount_one: '{{count}} informe',
      reportCount_other: '{{count}} informes',
      dateRange: 'Rango de fechas',
      occurrences: 'Apariciones',
      resolve: 'Resolver',
      openCommonIssue: 'Abrir problema común',
      adminRequired: 'Se requiere Administrador',
      resolveTitle: 'Resolver Producto desconocido {{sku}}',
      sourceAttributesUnavailable:
        'Sales Pivot no proporciona una unidad ni un peso neto autoritativos. Selecciónelos explícitamente al crear el Producto; el sistema no los adivina.',
      examples: 'Ejemplos de origen',
      matchExisting: 'Asignar a un Producto existente',
      searchProducts: 'Buscar por SKU, nombre o alias',
      decisionPreview: 'Vista previa de decisión y alias',
      canonicalAttributes: 'Unidad: {{unit}} · Peso neto: {{weight}}',
      notSet: 'sin definir',
      existingAliases_one: 'El Producto tiene {{count}} alias existente.',
      existingAliases_other: 'El Producto tiene {{count}} alias existentes.',
      reason: 'Motivo de la decisión',
      reasonHelp: 'El motivo se conserva en la auditoría y la procedencia del alias.',
      resolveFailed: 'No se pudo guardar la decisión ni revalidar los informes.',
      technicalDetail: 'Detalle técnico: {{code}}',
      resultSummary:
        'Asignación guardada. Total {{total}}, listos {{ready}}, aún bloqueados {{blocked}}, errores de revalidación {{failed}}.',
      previousAliases_one: 'Se encontró {{count}} alias anterior para este código de origen.',
      previousAliases_other:
        'Se encontraron {{count}} alias anteriores para este código de origen.',
      active: 'activo',
      leaveUnresolved: 'Dejar sin resolver',
      createNew: 'Crear Producto nuevo',
      resolving: 'Guardando y revalidando…',
      retryFailed: 'Reintentar validaciones fallidas',
      retrying: 'Revalidando de nuevo…',
      matchAndRevalidate: 'Asignar y revalidar informes',
    },
    storeIssues: {
      title: 'Nuevas tiendas y productos — tiendas',
      description: 'Vincula un nombre de origen o crea una tienda sin adivinar su red.',
      sourceIdentity: 'Nombre de tienda en origen',
      type: 'Tipo de importación',
      affectedReports: 'Informes afectados',
      dateRange: 'Rango de fechas',
      candidates: 'Posibles tiendas existentes',
      noCandidates: 'No hay candidato seguro',
      resolve: 'Añadir o vincular tienda',
      resolveTitle: 'Resolver tienda {{name}}',
      noAutomaticMatch: 'El sistema no cambiará automáticamente la tienda ni la red.',
      reportCount_one: '{{count}} informe',
      reportCount_other: '{{count}} informes',
      occurrenceCount_one: '{{count}} aparición',
      occurrenceCount_other: '{{count}} apariciones',
      reports: 'Informes y fechas',
      examples: 'Ejemplos del origen',
      matchExisting: 'Vincular una tienda existente',
      createNew: 'Añadir una tienda nueva',
      searchStores: 'Buscar por tienda o red',
      canonicalName: 'Nombre canónico de la tienda',
      shortName: 'Nombre corto (opcional)',
      network: 'Red',
      networkRequired: 'La red debe seleccionarse explícitamente.',
      manageNetworks: 'Abrir catálogo de redes',
      activityDateSeparate: 'La primera aparición no demuestra la fecha de apertura.',
      effectiveFrom: 'Alias vigente desde',
      effectiveTo: 'Alias vigente hasta (exclusivo, opcional)',
      aliasDateHelp: 'Este rango solo corresponde al mapeo del nombre de origen.',
      reason: 'Justificación obligatoria',
      reasonHelp: 'Actor, decisión, tienda, red y alias quedan registrados.',
      resolveFailed: 'No se pudo guardar la decisión o revalidar los informes.',
      resultSummary:
        'Total {{total}}, listos {{ready}}, bloqueados {{blocked}}, errores {{failed}}.',
      leaveUnresolved: 'Dejar sin resolver',
      saveAndRevalidate: 'Guardar y comprobar informe',
      revalidating: 'Guardando y revalidando…',
      retry: 'Reanudar validación fallida',
      locationOnlyWarning: '{{store}} ({{network}}) no es automáticamente la misma tienda.',
      matchBasis_EXACT_CANONICAL: 'nombre canónico exacto',
      matchBasis_EXACT_ALIAS: 'alias existente exacto',
      matchBasis_LOCATION_TOKEN: 'ubicación similar — requiere revisión',
    },
    distributionIssues: {
      title: 'Falta política de distribución',
      summary: 'Falta política de distribución: {{products}} Productos · {{reports}} informes',
      product: 'Producto',
      sourceSku: 'Código de origen',
      sourceName: 'Nombre de origen',
      sourceNames: 'Nombres de origen',
      scope: 'Alcance',
      dateRange: 'Fechas de informes',
      counts: 'Apariciones',
      currentPolicies: 'Políticas actuales',
      noPolicies: 'Ninguna',
      fixPolicy: 'Corregir política',
      resolveTitle: 'Definir política de distribución',
      explanation:
        'Falta la política de distribución del Producto {{sku}} {{product}} en {{network}}. Su presencia en un informe no autoriza crear una política.',
      reportCount_one: '{{count}} informe',
      reportCount_other: '{{count}} informes',
      occurrenceCount_one: '{{count}} aparición',
      occurrenceCount_other: '{{count}} apariciones',
      action: 'Resolución',
      actionCreate: 'Crear política nueva',
      actionExtend: 'Ampliar política actual',
      actionLink: 'Vincular política existente',
      existingPolicy: 'Política existente',
      wholeNetwork: 'Toda la red',
      selectedStores: 'Tiendas seleccionadas',
      affectedStores: 'Tiendas identificadas por informes',
      mode: 'Modelo de distribución',
      modeDirect: 'Reposición directa a Tiendas',
      modeCentral: 'Venta desde almacén central',
      effectiveFrom: 'Vigente desde',
      effectiveTo: 'Vigente hasta (exclusivo)',
      reason: 'Motivo obligatorio',
      reasonHelp: 'Actor, estado anterior, estado nuevo y motivo quedan en la auditoría.',
      previewTitle: 'Informes para revalidar: {{count}}',
      resolveFailed: 'No se pudo guardar la política o revalidar los informes.',
      result:
        'Revalidación terminada: total {{total}}, listos {{ready}}, aún bloqueados {{blocked}}, errores {{failed}}.',
      leaveUnresolved: 'Dejar sin resolver',
      saveAndRevalidate: 'Guardar y revalidar informes',
      revalidating: 'Guardando y revalidando…',
      retry: 'Continuar revalidación fallida',
      scopeKindNETWORK: 'toda la red',
      scopeKindSTORES: 'tiendas seleccionadas',
    },
    missing: {
      title: 'Informes faltantes',
      daysLate_one: '{{count}} día de retraso',
      daysLate_few: '{{count}} días de retraso',
      daysLate_many: '{{count}} días de retraso',
      daysLate_other: '{{count}} días de retraso',
      hoursLate: '{{count}} h de retraso',
      shipmentScheduleUnavailable:
        'Ventas usa el calendario diario autorizado. No hay calendario de Envíos configurado, por lo que no se inventan ausencias.',
      exportCsv: 'Exportar CSV',
      activeScope: '{{stores}} tiendas activas · {{networks}} redes',
      dailySchedule: 'Calendario diario de Ventas',
      openImport: 'Abrir importación',
      openCommonIssue: 'Corregir problema común',
      upload: 'Subir informe',
      emptyTitle: 'No faltan informes de Ventas',
      emptyBody: 'Cada día esperado del período tiene un informe confirmado.',
    },
    missingState: {
      EXPECTED_TODAY: 'Esperado hoy',
      OVERDUE: 'Vencido',
      IN_PROGRESS: 'Importación en curso',
    },
    history: {
      title: 'Historial de importaciones',
      open: 'Abrir',
      markTestData: 'Marcar como datos de prueba',
      markTestDataTitle: 'Marcar importación histórica como datos de prueba',
      markingTestData: 'Marcando…',
      testDataLabel: 'Etiqueta de datos de prueba',
      testDataLabelHelp: 'La etiqueta debe comenzar por «STAGING TEST».',
      testDataReason: 'Motivo',
      markerKeepsStatus:
        'Esta operación solo añade una marca inmutable de datos de prueba. Estado visible: {{displayStatus}}; estado de dominio: {{domainStatus}}. Los informes, hechos y el historial no cambiarán.',
      domainStatus: 'Estado de dominio: {{status}}',
      dispositionIncompleteSourceRejected: 'Disposición: origen incompleto rechazado',
      rejectionReason: 'Motivo: {{reason}}',
      markTestDataFailed: 'No se pudo marcar la importación como datos de prueba.',
      markTestDataSuccess: 'La importación se marcó como datos de prueba sin cambiar su historial.',
      cancel: 'Cancelar',
      emptyTitle: 'Sin historial',
      emptyBody: 'Ningún informe coincide con los filtros.',
    },
    recommended: {
      COMPARE_AND_RESOLVE: 'Comparar y resolver',
      RETRY_OR_REUPLOAD: 'Reintentar / revisar',
      FIX_MAPPINGS_AND_REVALIDATE: 'Corregir y revalidar',
    },
    status: {
      QUEUED: 'En cola',
      UPLOADED: 'Subido',
      PARSING: 'Leyendo',
      PROCESSING: 'Procesando',
      PREVIEW_READY: 'Listo para confirmar',
      READY: 'Listo',
      BLOCKED: 'Bloqueado',
      DUPLICATE: 'Duplicado',
      DATE_CONFLICT: 'Conflicto',
      CONFLICT: 'Conflicto',
      FAILED: 'Error',
      CONFIRMED: 'Confirmado',
      COMPLETED: 'Completado',
      COMPLETED_WITH_ERRORS: 'Completado con problemas',
      REVIEW_REQUIRED: 'Requiere revisión',
      CANCELLED: 'Cancelado',
      INCOMPLETE_SOURCE_REJECTED: 'Origen incompleto rechazado',
      REVALIDATION_FAILED: 'Error de revalidación',
      ROLLED_BACK: 'Revertido',
      REJECTED: 'Rechazado',
      SUPERSEDED: 'Sustituido',
      OVERLAP: 'Solapado — se conserva el informe',
      NOT_UPLOADED: 'Sin informe',
    },
    reason: {
      BULK_IMPORT_PROCESSING_FAILED: 'El procesamiento falló',
      SHIPMENT_INVOICE_NUMBER_REQUIRED: 'Falta el número de factura en la fila del envío.',
      SHIPMENT_INVOICE_NUMBER_UNSAFE_NUMERIC:
        'El número de factura se guardó en Excel como un número no seguro y puede haber perdido dígitos. Configure la columna Invoice # como Texto y vuelva a introducir el número original.',
      SHIPMENT_INVOICE_NUMBER_INVALID:
        'El número de factura tiene un tipo o formato no compatible. Formatea Invoice # como Texto.',
      SHIPMENT_INVOICE_NUMBER_TOO_LONG: 'El número de factura supera el límite de 128 caracteres.',
      DATE_CONFLICT: 'El informe confirmado contiene datos distintos',
      UNKNOWN_STORE: 'Tienda desconocida',
      AMBIGUOUS_STORE: 'Tienda ambigua',
      UNKNOWN_PRODUCT: 'Producto desconocido',
      AMBIGUOUS_PRODUCT: 'Producto ambiguo',
      MISSING_PRODUCT_DISTRIBUTION: 'Falta la distribución del producto',
      DISTRIBUTION_POLICY_MISSING: 'Falta la política de distribución del producto',
      CENTRAL_RECONCILIATION_UNSUPPORTED: 'La conciliación central no está soportada',
      BLOCKED: 'La validación está bloqueada',
      FAILED: 'La importación falló',
      SHIPMENT_MANUAL_RECONCILIATION_REQUIRED:
        'El envío requiere conciliación manual con un movimiento de almacén existente',
    },
    testData: 'Datos de prueba',
    summary: {
      total: 'Total',
      processing: 'Procesando',
      ready: 'Listos',
      blocked: 'Bloqueados',
      duplicate: 'Duplicados',
      conflict: 'Conflictos',
      overlapExistingConfirmed: 'Solapado conservado',
      failed: 'Errores',
      confirmed: 'Confirmados',
      cancelled: 'Cancelados',
    },
    uploadDurableBatch: 'El lote duradero está guardado. Ábrelo o sigue aquí cada archivo.',
    upload: {
      type: 'Tipo de importación',
      choose: 'Elegir archivos XLSX',
      limits: 'Hasta 100 archivos, 10 MB cada uno y 100 MB por lote.',
      drop: 'Suelta archivos XLSX aquí o elígelos',
      progress:
        '{{processed}} / {{total}} procesados · {{accepted}} aceptados · {{failed}} fallidos · {{invalid}} no válidos · {{ready}} listos · {{inProgress}} en curso · {{cancelled}} cancelados',
      technicalId: 'ID técnico: {{id}}',
      fileList: 'Archivos seleccionados',
      filename: 'Nombre',
      size: 'Tamaño',
      detectedType: 'Tipo detectado',
      detectedDate: 'Fecha detectada',
      state: 'Estado',
      actions: 'Acciones',
      afterValidation: 'Tras validación',
      retry: 'Reintentar',
      remove: 'Quitar',
      uploading: 'Subiendo…',
      start: 'Subir {{count}} archivos listos',
      retryFailed: 'Reintentar fallidos',
      cancelRemaining: 'Cancelar restantes',
    },
    uploadState: {
      CHECKING: 'Comprobando',
      READY: 'Listo',
      INVALID: 'No válido',
      UPLOADING: 'Subiendo',
      QUEUED: 'En cola',
      DUPLICATE: 'Fuente duplicada',
      FAILED: 'Error',
      CANCELLED: 'Cancelado',
    },
    errors: {
      invalidDate:
        'Introduzca un intervalo de fechas válido. La fecha final no puede ser anterior a la inicial.',
      BULK_IMPORT_FILE_COUNT_INVALID: 'El lote supera 100 archivos.',
      BULK_IMPORT_XLSX_REQUIRED: 'Solo se aceptan archivos XLSX.',
      BULK_IMPORT_FILE_SIZE_INVALID: 'El archivo debe tener entre 1 byte y 10 MB.',
      BULK_IMPORT_BATCH_SIZE_INVALID: 'El lote supera 100 MB.',
      DUPLICATE_SELECTED: 'El mismo archivo ya fue seleccionado.',
      BULK_IMPORT_DUPLICATE_SOURCE: 'Esta fuente ya existe en el lote.',
      UPLOAD_NETWORK_ERROR: 'La carga se interrumpió. Reintenta este archivo.',
      INTERNAL_ERROR:
        'No se pudo guardar el archivo (código: INTERNAL_ERROR). Reinténtalo; si el error se repite, facilita el ID técnico.',
      MASTER_DATA_LOCK_TIMEOUT:
        'Otra operación está actualizando los datos maestros. Actualiza los datos e inténtalo de nuevo.',
      retryFailed: 'El reintento no fue aceptado.',
      revalidateFailed: 'La revalidación falló.',
      confirmFailed: 'La confirmación masiva falló. No se forzó ningún informe.',
    },
  },
  shipmentWorkspace: {
    physicalQuantityBasis:
      'Las cantidades físicas incluyen las disposiciones aprobadas. Los informes anteriores sin disposición conservan la cantidad de la factura; esto no demuestra un movimiento físico.',
    sourceInvoiceQuantity: 'Cantidad original de factura',
    title: 'Envíos',
    description: 'Envíos reales confirmados a tiendas por fecha de envío.',
    openImports: 'Importaciones de envíos',
    activeRange: '{{from}} — {{to}}',
    confirmedOnly: 'Solo confirmados',
    activeFilters: 'Filtros activos: {{count}}',
    expandFilters: 'Mostrar filtros',
    collapseFilters: 'Ocultar filtros',
    datePreset: 'Periodo',
    weekPeriods: 'Semana',
    preset: {
      '7d': '7 días',
      '28d': '28 días',
      '90d': '90 días',
      ytd: 'Año actual',
      current_week: 'Semana actual',
      previous_week: 'Semana anterior',
    },
    isoYear: 'Año ISO',
    isoWeek: 'Semana ISO',
    isoWeekOption: 'Semana {{week}} · {{range}}',
    from: 'Desde',
    to: 'Hasta',
    network: 'Red',
    allNetworks: 'Todas las redes',
    store: 'Tienda',
    allStores: 'Todas las tiendas',
    productSearch: 'Buscar producto',
    product: 'Producto',
    allProducts: 'Todos los productos',
    orientation: 'Orientación',
    storesByProducts: 'Tiendas × productos',
    productsByStores: 'Productos × tiendas',
    totalShipped: 'Total enviado',
    storesCount: 'Tiendas: {{count}}',
    productsCount: 'Productos: {{count}}',
    factsCount: 'Líneas de origen: {{count}}',
    loadFailed: 'No se pudo cargar la matriz de envíos.',
    columns: 'Columnas',
    resetColumnLayout: 'Restablecer columnas',
    resetRowOrder: 'Restablecer orden',
    searchColumns: 'Buscar columnas',
    showAllColumns: 'Mostrar todas',
    hideAllColumns: 'Ocultar todas',
    allMatrixColumns: 'Todas las columnas',
    visibleColumnsCount: 'Visibles: {{visible}} / {{total}}',
    columnSearchResults: 'Resultados de búsqueda',
    showSearchResults: 'Mostrar resultados',
    hideSearchResults: 'Ocultar resultados',
    noColumnSearchResults: 'No hay columnas coincidentes',
    noProductsSelected: 'No se seleccionaron productos',
    noStoresSelected: 'No se seleccionaron tiendas',
    pinColumn: 'Fijar columna',
    matrix: 'Matriz de envíos',
    rowLabel: 'Tienda / producto',
    sum: 'TOTAL',
    empty: 'No hay envíos confirmados a tiendas en el periodo seleccionado.',
    noShipment: 'Sin envío',
    unit: { PIECE: 'uds.', KG: 'kg', TRAY: 'bandeja' },
    drillDown: 'Detalles del envío',
    byDay: 'Por día',
    date: 'Fecha de envío',
    quantity: 'Cantidad',
    sourceEvidence: 'Líneas de origen inmutables',
    source: 'Origen',
  },
  shipmentImports: {
    approvedRepair: {
      title: 'Plan completo de reparación del envío',
      explanation:
        'Solo administradores. La vista previa lee todo el lote y el catálogo actual; aplicar ejecuta un plan atómico, sin confirmar el envío.',
      loading: 'Leyendo todas las líneas y los datos actuales…',
      drift:
        'El lote, las rutas, las políticas o el producto cambiaron. No se guardó nada; prepara otra vista previa.',
      saved: 'Plan guardado con auditoría',
      scope:
        '80907: PIECE→KG; cuatro rutas; 80862/80865: MAIN→ELDHUS desde el 1 de octubre; políticas Prís; dos correcciones de factura −528 sin movimiento físico. Las entregas +180 siguen independientes.',
      preview: 'Preparar vista previa actualizada del plan completo',
      issueResult: 'Problemas: {{before}} antes y {{after}} después del plan.',
      decisions:
        'Rutas: {{routes}} · transiciones: {{transitions}} · políticas: {{policies}} · decisiones físicas: {{physical}} · cambio de unidad: 1',
      remainingNegatives: 'Líneas negativas restantes sin decisión: {{count}}.',
      effectZero:
        'Esta acción crea 0 informes, hechos o movimientos; no confirma ni ejecuta KEEP/REPLACE.',
      noConfirmation:
        'Esto solo repara asignaciones y registra decisiones. El solapamiento y las demás líneas negativas aún pueden bloquear la confirmación.',
      reason: 'Justificación del administrador',
      confirmationHint: 'Escribe exactamente',
      confirmation: 'Confirmación del plan',
      close: 'Cerrar',
      apply: 'Aplicar el Repair Plan completo',
    },
    repairRoute: {
      title: 'Cambiar rutas globales de envío aprobadas',
      help: 'Plan de reparación auditado solo para Admin. Se limita a los SKU 80862 y 80865: cierra las rutas MAIN abiertas exactas e inicia ELDHUS el 2026-10-01. Ambos cambios son atómicos; el envío no se confirma.',
      failed:
        'No se pudo preparar o aplicar el cambio de ruta. No se guardó ningún cambio parcial.',
      saved: 'El cambio de ruta se guardó con auditoría y outbox; el envío sigue sin confirmarse.',
      loading: 'Comprobando revisiones actuales de rutas y almacenes…',
      drift:
        'Las rutas globales actuales o el almacén ELDHUS activo difieren del estado aprobado. La acción no está disponible.',
      revision: 'revisión esperada {{revision}}',
      reason: 'Motivo aprobado por el propietario',
      preview: 'Vista previa del cambio atómico',
      previewSummary: 'Incidencias previstas: {{before}} antes y {{after}} después.',
      confirmation: 'Confirmación exacta',
      close: 'Cerrar',
      apply: 'Aplicar Repair Plan',
      saving: 'Guardando…',
    },
    incremental: {
      title: 'Importación incremental de envíos',
      explanation:
        'Las repeticiones exactas con factura se conservan como evidencia de origen y se omiten. Solo los envíos nuevos pueden crear hechos y movimientos.',
      repeated: 'Filas de origen repetidas: {{count}}',
      new: 'Nuevas entregas: {{count}}',
      decision: 'Requieren una decisión: {{count}}',
      blocked:
        'Las filas modificadas o ambiguas requieren una conciliación explícita; nada se sustituirá en silencio.',
      confirm: 'Omitir repeticiones y añadir solo nuevos envíos',
      confirmationSummary:
        'Listo para confirmar: {{added}} filas nuevas; repeticiones omitidas: {{repeated}}; modificadas: {{changed}}; correcciones aprobadas: {{corrections}}.',
      recipientCorrection: 'Corrección de destinatarios: {{totals}}',
      warehouseCorrection: 'Corrección de almacenes: {{totals}}',
      canonicalUnchanged: 'El informe confirmado existente no se sustituirá.',
    },
    physical: {
      stale:
        'Los datos cambiaron desde la aprobación. La versión anterior permanece en el historial; se requiere una nueva vista previa y aprobación.',
      reapprove: 'Preparar una nueva versión de disposición',
      title: 'Clasificación de movimientos físicos',
      help: 'Las cantidades y facturas de origen permanecen inmutables. Las decisiones solo controlan movimientos físicos futuros; guardar no confirma el envío.',
      failed:
        'La operación falló. Compruebe el alcance y el estado actual del lote; ningún envío se confirmó automáticamente.',
      loading: 'Cargando…',
      saved: 'Decisiones guardadas con auditoría. El envío no se confirmó automáticamente.',
      noNegative: 'No hay líneas negativas para clasificar.',
      source: 'Origen / neto de factura',
      kind: 'Tipo de evento',
      choose: 'Seleccione un evento',
      original: 'Línea positiva vinculada',
      linkHelp:
        'Seleccione solo un vínculo confirmado. La misma cantidad no demuestra una relación.',
      priorFact: 'Fact confirmado de un informe anterior (corrección heredada)',
      priorFactHelp:
        'Use solo un ID de fact verificado por un administrador. Conserva evidencia a nivel de informe/transacción, nunca un movimiento histórico supuesto.',
      unlinked:
        'Factura original no identificada: se omiten movimientos solo para la línea negativa seleccionada. Las demás entregas positivas permanecen intactas.',
      reason: 'Motivo de la decisión',
      summary: 'Origen y efectos físicos',
      movementCount: 'Movimientos futuros del registro: {{count}}',
      unit: 'Unidad',
      destinationNet: 'Cambio de destinatarios',
      warehouseNet: 'Cambio de almacenes de origen',
      confirmation: 'Confirmación explícita del alcance',
      close: 'Cerrar',
      preview: 'Vista previa de efectos',
      saving: 'GUARDANDO…',
      save: 'Guardar decisiones — sin confirmar el envío',
      CANCEL_BEFORE_DISPATCH:
        'Anulación antes del envío — sin movimientos para ambas líneas vinculadas',
      INVOICE_ONLY_NO_DISPATCH: 'Error de factura sin envío — línea original no identificada',
      DISPOSAL: 'Eliminación en el destinatario — sin entrada al almacén',
      RETURN_TO_STOCK: 'Devolución física — entrada al almacén de origen',
    },
    title: 'Envíos oficiales',
    description: 'Cargue, valide, confirme y revierta libros oficiales de envíos.',
    upload: 'Cargar libro',
    history: 'Historial de importación',
    filename: 'Archivo',
    shipmentDates: 'Fechas de envío',
    status: 'Estado',
    uploaded: 'Cargado',
    destinations: 'Destinos',
    products: 'Productos',
    lines: 'Líneas de envío',
    total: 'Cantidad total',
    ledger: 'Libro de inventario',
    pipeline: 'Pipeline de red',
    preview: 'Vista previa y validación de mapeos',
    issues: 'Problemas bloqueantes',
    date: 'Fecha',
    destination: 'Destino',
    destinationType: 'Tipo de destino',
    product: 'Producto',
    warehouse: 'Almacén de origen',
    warehouseRouteMissing: 'Falta la ruta de almacén',
    warehouseSourceDestinationMatch: 'El almacén de origen y destino deben ser distintos',
    confirm: 'Confirmar informe oficial de envíos',
    revalidate: 'Revalidar mapeos',
    resolveStore: 'Añadir o vincular tienda',
    rollback: 'Revertir informe',
    rollbackReason: 'Motivo obligatorio de reversión',
    detail: 'Detalle del informe de envíos',
    duplicate: 'Este libro duplica evidencia de envíos existente y no se puede confirmar.',
    conflict: 'La superposición con un envío confirmado requiere conciliación antes de confirmar.',
    semantics:
      'Un envío directo positivo aumenta la tienda y reduce su almacén de origen. Una entrega a almacén aumenta el almacén de destino y reduce el de origen. Un envío central reduce el almacén y solo registra pipeline. Las líneas centrales negativas se bloquean.',
    choose: 'Seleccione un libro oficial de envíos .xlsx',
    back: 'Volver al historial',
    download: 'Descargar original',
    unconfirmedDates: 'Las fechas pasan a ser oficiales solo tras la confirmación',
    matchProduct: 'Vincular producto',
    canonicalProduct: 'Producto canónico',
    matchProductHelp:
      'La vinculación guarda un alias manual de origen. El informe permanece bloqueado hasta revalidarlo.',
    newProductUnit: 'Unidad del nuevo producto',
    createProduct: 'Crear nuevo producto',
    matchExistingProduct: 'Vincular producto existente',
    leaveUnresolved: 'Dejar sin resolver',
    operationFailed: 'No se pudo completar la importación de envíos.',
    failure: {
      SHIPMENT_HEADER_INVALID: 'El archivo de envíos tiene encabezados no válidos.',
      SHIPMENT_DATE_INVALID: 'El archivo de envíos contiene una fecha no válida.',
      SHIPMENT_INVOICE_NUMBER_REQUIRED: 'Falta el número de factura en una línea de envío.',
      SHIPMENT_INVOICE_NUMBER_UNSAFE_NUMERIC:
        'El número de factura se guardó en Excel como un número no seguro y puede haber perdido dígitos. Configure la columna Invoice # como Texto y vuelva a introducir el número original.',
      SHIPMENT_INVOICE_NUMBER_INVALID:
        'El número de factura tiene un tipo o formato no compatible. Configure la columna Invoice # como Texto.',
      SHIPMENT_INVOICE_NUMBER_TOO_LONG:
        'El número de factura es demasiado largo. La longitud máxima es de 128 caracteres.',
      SHIPMENT_QUANTITY_MISSING: 'Falta la cantidad en una línea de envío.',
      SHIPMENT_REQUIRED_VALUE_MISSING: 'Falta un valor obligatorio en el archivo de envíos.',
      SHIPMENT_DESTINATION_INVALID: 'El archivo de envíos contiene un destino no válido.',
      SHIPMENT_PRODUCT_INVALID: 'El archivo de envíos contiene un valor de producto no válido.',
      SHIPMENT_INVALID_NUMERIC_CELL: 'El archivo de envíos contiene una cantidad no válida.',
      SHIPMENT_FORMULA_OR_RICH_CELL:
        'Los campos obligatorios del envío deben contener valores, no fórmulas.',
      SHIPMENT_ZERO_QUANTITY: 'Una línea de envío no puede tener cantidad cero.',
      SHIPMENT_WORKSHEET_UNEXPECTED: 'El archivo de envíos debe contener una hoja Netto.',
      SHIPMENT_LINES_MISSING: 'El archivo de envíos no contiene líneas de envío.',
      SHIPMENT_MALFORMED_XLSX: 'No se pudo leer el libro de envíos.',
      SHIPMENT_FILE_SIZE_INVALID: 'El archivo de envíos está vacío o supera el tamaño permitido.',
      SHIPMENT_NOT_XLSX: 'La fuente de envíos no es un archivo XLSX válido.',
      SHIPMENT_XLSX_LIMIT_EXCEEDED:
        'El libro de envíos supera los límites seguros de procesamiento.',
      SHIPMENT_UNSAFE_XLSX_CONTENT: 'El libro de envíos contiene contenido inseguro no admitido.',
      SHIPMENT_STAGING_FAILED: 'No se pudieron preparar los datos de envío para validarlos.',
      SHIPMENT_PARSE_FAILED: 'No se pudieron leer los datos del archivo de envíos.',
    },
  },
  production: {
    title: 'Planificación de producción',
    planningOnly:
      'Planifique la producción, registre los hechos reales y contabilice inventario solo al completar.',
    product: 'Producto',
    date: 'Fecha de producción',
    location: 'Ubicación',
    planningUnit: 'Unidad de planificación',
    plannedQuantity: 'Cantidad planificada',
    quantity: 'Cantidad',
    status: 'Estado',
    draft: 'Borrador',
    planned: 'Planificado',
    actions: 'Acciones',
    openOrder: 'Abrir',
    loading: 'Cargando orden de producción…',
    close: 'Cerrar',
    batchPending: 'Lote pendiente',
    plannedVsActual: 'Salida planificada / real',
    actualsNoLedger:
      'Los hechos reales son provisionales. El inventario se contabiliza solo al completar.',
    actualOutput: 'Salida real',
    actualInputs: 'Entradas reales',
    actual: 'Real',
    variance: 'Variación',
    batch: 'Lote',
    expiry: 'Consumir antes de',
    actualMaterial: 'Material real',
    actualQuantity: 'Cantidad real',
    waste: 'Merma de proceso',
    wasteCategory: 'Categoría de merma',
    stage: 'Etapa',
    machine: 'Máquina/recurso',
    stageStartedAt: 'Inicio de etapa',
    stageEndedAt: 'Fin de etapa',
    operationMinutes: 'Minutos de operación',
    people: 'Personas',
    saveActuals: 'Guardar datos reales',
    timingSummary: 'Transcurrido: {{elapsed}} min · operación: {{operation}} min',
    start: 'Iniciar',
    completeReview: 'Completar — contabilizar efectos de inventario revisados',
    cancel: 'Cancelar',
    reverse: 'Revertir',
    inventoryEffectsPosted: '{{count}} efectos de inventario contabilizados',
    orderStatus: {
      DRAFT: 'Borrador',
      PLANNED: 'Planificado',
      IN_PROGRESS: 'En curso',
      COMPLETED: 'Completado',
      CANCELLED: 'Cancelado',
      REVERSED: 'Revertido',
    },
    savePlan: 'Guardar plan',
    saveFailed: 'No se pudo guardar el plan.',
    readOnly: 'Tienes acceso de solo lectura.',
    requirementsPreview: 'Vista previa de requisitos',
    finishedOutput: 'Producto terminado',
    requirement: 'Requisito',
    kind: 'Tipo',
    rolls: 'Rollos completos',
    weeklyRequirements: 'Necesidades semanales',
    previousWeek: 'Semana anterior',
    currentWeek: 'Semana actual',
    nextWeek: 'Semana siguiente',
    noPlans: 'No hay planes en la semana seleccionada.',
    selectedProduct: 'el producto seleccionado',
    configurationHint:
      'Para planificar {{product}}, un administrador debe configurar una receta versionada y un formato de embalaje. Materiales disponibles: {{materials}}.',
    configuration: 'Configuración de planificación',
    configurationWorkspaceHelp:
      'Configure recetas, embalaje, film y etapas de producción en el único espacio de costes. El planificador sigue siendo sencillo.',
    openMaterials: 'Abrir materiales',
    openProductConfiguration: 'Configurar producto',
    readyToPlan: 'LISTO PARA PLANIFICAR',
    configurationMissing: 'FALTA CONFIGURACIÓN',
    configurationChecklist: {
      recipe: 'Receta',
      output: 'Rendimiento',
      packaging: 'Embalaje',
      multivac: 'Multivac',
      stages: 'Etapas de producción',
    },
    noConfiguredStages: 'No hay etapas de producción cronometradas configuradas.',
    newMaterial: 'Nuevo material',
    materialCode: 'Código de material',
    materialName: 'Nombre de material',
    rollLengthMetres: 'Longitud del rollo (m)',
    stockManaged: 'Gestionado en inventario',
    costManaged: 'Gestionado por costes',
    unit: 'Unidad',
    createMaterial: 'Añadir material',
    recipeVersion: 'Versión de receta',
    referenceOutput: 'Salida de referencia',
    ingredient: 'Ingrediente',
    ingredientQuantity: 'Cantidad de ingrediente',
    createRecipe: 'Añadir receta',
    formatVersion: 'Formato de embalaje',
    finishedPerUnit: 'Resultado por unidad',
    finishedUnit: 'Unidad terminada',
    packsPerUnit: 'Paquetes por unidad',
    packagingMaterial: 'Material de embalaje',
    packagingQuantity: 'Cantidad de embalaje',
    packsPerCycle: 'Paquetes por ciclo',
    pitchMetres: 'Avance de máquina (m)',
    lowerFilm: 'Película inferior',
    upperFilm: 'Película superior',
    createFormat: 'Añadir formato',
    chooseProductFirst: 'elige primero un producto',
    missing: {
      PRODUCTION_FORMAT_REQUIRED: 'No hay formato de embalaje activo para esta unidad y fecha.',
      PRODUCTION_RECIPE_REQUIRED: 'No hay receta activa para este producto y fecha.',
      PRODUCTION_RECIPE_OUTPUT_UNIT_MISMATCH:
        'La unidad de salida de la receta no coincide con el formato. Configura una relación explícita.',
      PRODUCTION_FILM_CONFIGURATION_REQUIRED:
        'La configuración de película requiere paquetes por ciclo y avance de máquina.',
      PRODUCTION_FILM_ROLL_LENGTH_REQUIRED:
        'La película seleccionada requiere una longitud de rollo configurada.',
      generic: 'Falta la configuración de producción necesaria.',
    },
    requirementKinds: {
      INGREDIENT: 'Ingrediente',
      PACKAGING: 'Embalaje',
      LOWER_FILM: 'Película inferior',
      UPPER_FILM: 'Película superior',
    },
  },
  dashboard: {
    title: 'Panel operativo',
    description: 'Solo lectura: actualidad de datos, ventas y atención de stock.',
    asOf: 'A fecha de',
    network: 'Red',
    allNetworks: 'Todas las redes',
    store: 'Tienda',
    allStores: 'Todas las tiendas',
    loadFailed: 'No se pudo cargar el panel.',
    dataStatus: 'Estado de datos',
    latestSales: 'Última venta confirmada: {{date}}',
    coverage: 'Cobertura de 28 días: {{status}}',
    missingSalesDays: 'Días de venta faltantes: {{count}}',
    stockIncomplete: 'Stock incompleto: {{count}}',
    stockNotInitialized: 'Stock no inicializado: {{count}}',
    latestSalesImport: 'Última importación de ventas: {{date}}',
    latestShipmentImport: 'Última importación de envíos: {{date}}',
    importsActionRequired: 'Importaciones que requieren acción: {{count}}',
    importsMissing: 'Informes faltantes: {{count}} (Ventas {{sales}}, Envíos {{shipments}})',
    oldestImportProblem: 'Problema de importación más antiguo: {{date}}',
    salesSnapshot: 'Resumen de ventas',
    period: 'Periodo',
    knownQuantity: 'Cantidad conocida',
    coverageLabel: 'Cobertura',
    activeScope: 'Ámbito activo',
    storesProducts: '{{stores}} tiendas · {{products}} productos',
    noSalesAccess: 'Los detalles de ventas no están disponibles para este usuario.',
    stockSummary: 'Resumen de atención de stock',
    lowStock: 'Bajo / reposición: {{count}}',
    stockNotManaged: 'Gestionado centralmente: {{count}}',
    noStockAccess: 'Los detalles de stock no están disponibles para este usuario.',
    salesTrend: 'Tendencia de ventas conocidas',
    salesTrendDescription:
      'Cada marca es una cantidad diaria conocida. ? es un día de ventas entero faltante, nunca cero.',
    stockAttention: 'Atención de stock',
    product: 'Producto',
    current: 'Actual',
    goal: 'OBJETIVO',
    difference: 'Diferencia',
    reorder: 'Reposición',
    state: 'Estado',
    notNumeric: 'DESCONOCIDO',
    provisional: 'provisional',
    noStockAttention: 'No hay elementos de tienda directa que requieran atención.',
    topProducts: 'Productos principales por ventas conocidas',
    storesCount: '{{count}} tiendas',
    dataIssues: 'Problemas de datos',
    noDataIssues: 'No hay problemas de datos actuales.',
    recentImports: 'Actividad de importación reciente',
    coverageState: {
      COMPLETE: 'COMPLETA',
      INCOMPLETE_DATA: 'DATOS INCOMPLETOS',
    },
    periods: {
      LATEST_CONFIRMED_DAY: 'Último día confirmado',
      LAST_7_DAYS: 'Últimos 7 días',
      LAST_28_DAYS: 'Últimos 28 días',
    },
    issues: {
      MISSING_SALES_DAYS: '{{count}} días de informe de ventas faltantes',
      STOCK_INCOMPLETE_DATA: '{{count}} celdas de stock con datos incompletos',
      STOCK_NOT_INITIALIZED: '{{count}} celdas de stock no inicializadas',
      IMPORT_REVIEW_REQUIRED: '{{count}} lotes de importación requieren revisión',
    },
  },
  operations: {
    title: 'Operaciones',
    previous: 'Semana anterior',
    current: 'Semana actual',
    next: 'Semana siguiente',
    loadFailed: 'No se pudieron cargar las operaciones.',
    brief: 'Resumen semanal',
    briefText:
      'Esta semana: {{planned}} planificados, {{inProgress}} en curso, {{completed}} completados. Días de ventas faltantes: {{missingSalesDays}}. Decisiones de envíos pendientes: {{shipmentOwnerDecisions}}.',
    production: 'Producción esta semana',
    openProduction: 'Abrir producción',
    openWarehouse: 'Abrir almacén',
    storeOrders: 'Pedidos de tiendas',
    openStoreOrders: 'Abrir pedidos',
    storeOrdersCoverage:
      '{{confirmed}} / {{expected}} tiendas confirmadas · {{missing}} pendientes',
    forecast: 'Pronóstico de la próxima semana',
    openForecast: 'Abrir pronóstico',
    forecastUnavailable: 'Todavía no hay una ejecución de pronóstico.',
    lowConfidence: '{{count}} filas de baja confianza',
    date: 'Fecha',
    product: 'Producto',
    quantity: 'Cantidad',
    location: 'Ubicación',
    statusLabel: 'Estado',
    moveDepartment: 'Mover a departamento',
    noOrders: 'No hay órdenes de producción esta semana.',
    requirements: 'Necesidades semanales',
    requirement: 'Necesidad',
    kind: 'Tipo',
    rolls: 'Rollos',
    noRequirements: 'No hay necesidades planificadas.',
    attention: 'Necesita atención',
    open: 'Abrir',
    noIssues: 'No hay problemas operativos actuales.',
    issue: {
      missingSalesCoverage: 'Falta el informe de ventas de {{count}} día(s).',
      shipmentUnconfirmed: 'Un informe de envíos sigue sin confirmar.',
      shipmentProductIdentities:
        '{{count}} decisión(es) de identidad de producto de envíos sin resolver.',
      shipmentWarehouseRoutes: '{{count}} ruta(s) de almacén de origen de envíos sin resolver.',
    },
    status: {
      DRAFT: 'Borrador',
      PLANNED: 'Planificado',
      IN_PROGRESS: 'En curso',
      COMPLETED: 'Completado',
      CANCELLED: 'Cancelado',
      REVERSED: 'Revertido',
    },
  },
  forecast: {
    title: 'Pronóstico',
    model: 'Modelo {{version}} · Ventas hasta {{through}}',
    noRun: 'Todavía no hay una ejecución de pronóstico.',
    noRunEditor:
      'Todavía no hay pronóstico. Recálculalo desde ventas autorizadas cuando estén listas.',
    horizon: {
      7: 'Próximos 7 días',
      14: 'Próximos 14 días',
      28: 'Próximos 28 días',
    },
    recalculate: 'Recalcular pronóstico',
    recalculateFailed: 'No se pudo recalcular el pronóstico.',
    addPromotion: 'Añadir promoción',
    editPromotion: 'Editar promoción',
    productSearch: 'Buscar producto',
    product: 'Producto',
    store: 'Tienda',
    network: 'Red',
    all: 'Todos',
    date: 'Fecha',
    baseline: 'Base',
    promotion: 'Promoción',
    final: 'Pronóstico final',
    confidence: 'Confianza',
    confidenceValue: {
      HIGH: 'Alta',
      MEDIUM: 'Media',
      LOW: 'Baja',
      NO_DATA: 'Sin datos',
    },
    manual: 'Manual',
    manualOverride: 'Anulación manual del pronóstico',
    overrideQuantity: 'Cantidad final ({{unit}})',
    reason: 'Motivo',
    saveOverride: 'Guardar anulación',
    explain: 'Explicar',
    drivers: 'Factores',
    close: 'Cerrar',
    scope: 'Alcance',
    from: 'Desde',
    to: 'Hasta',
    uplift: 'Incremento manual (%)',
    description: 'Descripción',
    save: 'Guardar',
    promotions: 'Promociones',
    noPromotions: 'Sin promociones.',
    noUplift: 'Sin incremento manual',
    noRows: 'No hay pronósticos para los filtros seleccionados.',
    noData: 'Sin datos',
    loadFailed: 'No se pudo cargar el pronóstico.',
  },
  costing: {
    recipeImportAction: 'Importar receta',
    recipeImportTitle: 'Importar receta desde un documento',
    recipeImportHelp:
      'Leeremos los ingredientes de {{product}}. Se reutilizarán los materiales existentes y los que falten se crearán sin precio.',
    recipeImportChooseFile: 'Elegir un archivo DOCX o PDF',
    recipeImportFileTooLarge: 'El archivo supera los 8 MB.',
    recipeImportReading: 'Leyendo…',
    recipeImportPreview: 'Leer y previsualizar',
    recipeImportNotReady: 'Se requiere una previsualización válida.',
    recipeImportOutputRequired: 'Introduce un rendimiento final positivo.',
    recipeImportExistingCount: 'Coincidencias existentes: {{count}}',
    recipeImportNewCount: 'Materiales nuevos: {{count}}',
    recipeImportMissingPrices:
      'Los materiales nuevos se guardarán sin precio. El cálculo seguirá parcial hasta completar sus costes.',
    recipeImportOutputQuantity: 'Rendimiento final de la receta',
    recipeImportOutputUnit: 'Unidad de rendimiento',
    recipeImportOutputHelp:
      'Introduce la cantidad real de producto terminado obtenida con esta receta.',
    recipeImportSaving: 'Guardando…',
    recipeImportSave: 'Crear materiales y guardar receta',
    recipeImportStatus: {
      MATCHED: 'Existente',
      NEW: 'Nuevo, sin precio',
      UNIT_CONFLICT: 'Conflicto de unidad',
      AMBIGUOUS_MATCH: 'Ambiguo',
    },
    recipeImportWarnings: {
      PDF_TEXT_UNAVAILABLE: 'El PDF no contiene texto extraíble.',
      DOCX_DOCUMENT_XML_MISSING: 'No se pudo leer la estructura del documento DOCX.',
      NO_RECIPE_INGREDIENTS_DETECTED: 'No se detectó una tabla de ingredientes y cantidades.',
      UNSUPPORTED_RECIPE_FILE_TYPE: 'Se admiten archivos DOCX y PDF con texto.',
    },
    title: 'Calculadora de costes de producto',
    subtitle: 'Economía universal estándar, lote real e I+D.',
    mode: 'Modo',
    standard: 'Estándar',
    actual: 'Lote real',
    draft: 'Borrador / I+D',
    date: 'Fecha de cálculo',
    product: 'Producto',
    batch: 'Lote completado',
    select: 'Seleccionar',
    draftName: 'Nombre del borrador',
    outputQuantity: 'Cantidad de salida',
    output: 'Salida',
    unit: 'Unidad',
    material: 'Material',
    temporaryMaterial: 'Material temporal',
    quantity: 'Cantidad',
    unitCost: 'Coste unitario',
    addMaterial: 'Añadir material / embalaje',
    addAndUse: 'Añadir a la base y usar',
    useOnlyThisCalculation: 'Usar solo en este cálculo',
    materialKind: 'Tipo',
    ingredient: 'Ingrediente',
    packaging: 'Embalaje',
    explicitConsumable: 'Consumible explícito',
    stockManaged: 'Gestionado en inventario',
    lowerFilm: 'Lámina inferior',
    upperFilm: 'Lámina superior',
    labor: 'Mano de obra',
    machine: 'Máquina/recurso',
    addLine: 'Añadir componente de coste',
    removeLine: 'Eliminar',
    cancel: 'Cancelar',
    component: 'Componente',
    componentType: 'Tipo de componente',
    total: 'Total',
    state: 'Estado',
    summary: 'Economía',
    batchCost: 'Coste del lote',
    prices: 'Precios netos candidatos',
    pricesHint: 'Separa precios con comas',
    targetMargin: 'Margen objetivo (%)',
    targetPrice: 'Precio neto requerido',
    whatIf: 'Cambio de coste hipotético (%)',
    scenarioCost: 'Coste de escenario',
    sellingPrice: 'Precio neto de venta',
    profit: 'Beneficio / unidad',
    margin: 'Margen',
    markup: 'Recargo',
    batchProfit: 'Beneficio del lote',
    calculationUnavailable: 'Falta una configuración o coste requerido.',
    notCalculated: 'No calculado',
    notStarted: 'Sin iniciar',
    materials: 'Materias primas / receta',
    production: 'Producción',
    materialsDescription: 'Ingredientes y entradas de receta para este cálculo.',
    productionDescription:
      'Tiempo operativo registrado o de escenario, mano de obra y coste de máquina.',
    packagingDescription: 'Embalaje, lámina inferior, lámina superior y consumibles explícitos.',
    sectionNavigation: 'Secciones del cálculo de costes',
    stage: 'Etapa',
    operatingTime: 'Tiempo operativo',
    provenance: 'Base de coste',
    noComponents: 'No hay componentes en esta sección.',
    noOperationalStages: 'No hay etapas operativas disponibles.',
    addIngredient: 'Añadir ingrediente',
    addPackaging: 'Añadir embalaje',
    addStage: 'Añadir etapa',
    recipeStagesHelp:
      'Las etapas temporizadas opcionales usan las tarifas vigentes de mano de obra y máquina en el coste total del lote.',
    stageCode: 'Código de etapa',
    minutes: 'Minutos',
    people: 'Personas',
    materialsSubtotal: 'Subtotal de materias primas',
    productionSubtotal: 'Subtotal de producción',
    packagingSubtotal: 'Subtotal de embalaje',
    draftIdentity: 'Escenario local de I+D: no modifica evidencia operativa.',
    standardIdentity: 'Receta estándar resuelta y base de coste efectiva.',
    actualIdentity: 'Hechos del lote completado y base de coste histórica efectiva.',
    currency: 'Moneda',
    costSummary: 'Resumen del coste del lote',
    incompleteCost: 'Se muestra el importe conocido, pero falta uno o más costes obligatorios.',
    unitCostCard: 'Coste por unidad',
    perUnit: 'Por 1 {{unit}}',
    authoritativeUnitsOnly: 'Solo se muestran unidades con conversión autorizada explícita.',
    sellingEconomics: 'Precio de venta y margen',
    addPrice: 'Añadir precio',
    priceUnit: 'ISK neto por {{unit}}',
    calculatePrice: 'Calcular precio',
    scenarios: 'Escenarios (hipotéticos)',
    scenariosDescription:
      'Solo comparación local; los escenarios no persisten ni contabilizan evidencia.',
    scenario: 'Escenario',
    baselineScenario: 'ESC 1 — BASE',
    whatIfScenario: 'HIPOTÉTICO',
    costChange: 'Cambio de coste: {{percent}}%',
    totalCost: 'Coste total unitario',
    batchRevenue: 'Ingresos del lote',
    priceComparison: 'Comparación de precios candidatos',
    libraryTitle: 'Biblioteca de costes de producto',
    libraryDescription:
      'Modelos de coste de producto persistentes y versionados. Guardar no contabiliza inventario.',
    searchModels: 'Buscar productos y modelos',
    department: 'Departamento',
    allDepartments: 'Todos los departamentos',
    productOtherDepartment: 'EL PRODUCTO PERTENECE A OTRO DEPARTAMENTO',
    favorites: 'Favoritos',
    favorite: 'Favorito',
    rnd: 'I+D',
    noSavedVersion: 'Sin versión guardada',
    noSavedModels: 'No hay modelos de coste guardados que coincidan con los filtros.',
    selectSavedModel: 'Seleccione un modelo guardado para abrir su cálculo e historial.',
    versionHistory: 'Historial de versiones inmutable',
    immutableSnapshots:
      'Las versiones guardadas son evidencia inmutable; recalcular crea una versión con los costes efectivos actuales.',
    reopen: 'Reabrir',
    recalculate: 'Recalcular',
    useAsTemplate: 'Usar como plantilla',
    copy: 'copia',
    saveDraft: 'Guardar borrador',
    saveNewVersion: 'Guardar nueva versión',
    newProductModel: 'Modelo de coste de producto',
    approveStandardCost: 'Aprobar como coste estándar',
    approvalHelp:
      'Crea explícitamente el coste estándar de Producto con fecha efectiva utilizado por Rentabilidad. No contabiliza inventario.',
    effectiveFrom: 'Válido desde',
    reason: 'Motivo',
    approve: 'Aprobar',
    costingChecklist: 'Lista de comprobación de costes',
    versionComparison: 'Actual {{current}} · anterior {{previous}}',
    productLibrary: 'Productos',
    costInputBase: 'Base de costes',
    excludedProducts: 'Excluidos',
    excludeFromCosting: 'Excluir del cálculo de costes',
    restoreCosting: 'Restaurar al cálculo de costes',
    openProductEditor: 'Abrir editor',
    unassignedDepartment: 'Sin asignar',
    code: 'Código',
    currentCost: 'Coste actual',
    costHistory: 'Historial de costes',
    actions: 'Acciones',
    setCost: 'Establecer coste',
    setRate: 'Establecer tarifa',
    rateAmount: 'Tarifa',
    saveRate: 'Guardar tarifa',
    costNotManaged: 'Coste no gestionado',
    complete: 'Completo',
    missingCost: 'Falta coste',
    hour: 'hora',
    noRates: 'No hay tarifas activas.',
    purchaseAmount: 'Importe de compra',
    purchaseQuantity: 'Cantidad de compra',
    derivedUnitCost: 'El coste unitario exacto se calcula de esta compra.',
    sourceReference: 'Fuente / referencia',
    fxRate: 'Tipo de cambio a ISK',
    saveCost: 'Guardar coste',
    filmRollCostHelp: 'El precio del rollo se divide por su longitud: {{length}}.',
    requiredMetres: 'Necesario: {{metres}}',
    practicalRolls: 'rollos prácticos: {{count}} × {{length}}',
    rollLength: 'Longitud del rollo (m)',
    canonicalConfiguration: 'Configuración de producción',
    canonicalConfigurationHelp:
      'Solo crea una nueva versión de la Receta o Formato de Producción existente.',
    recipeLines: 'Líneas de receta',
    saveRecipeVersion: 'Guardar versión de receta',
    planningUnit: 'Unidad de planificación',
    packsPerPlanningUnit: 'Envases por unidad de planificación',
    packagingLines: 'Líneas de embalaje',
    filmMultivac: 'Film / Multivac',
    packsPerCycle: 'Envases por ciclo',
    pitchMetres: 'Paso (metros)',
    filmConfigurationRequired: 'El film requiere envases por ciclo y paso.',
    saveFormatVersion: 'Guardar versión de formato',
    standardShort: 'EST',
    archive: 'Archivar',
    materialsMasterTitle: 'Materiales e ingredientes',
    materialsMasterDescription:
      'Una base compartida de materiales para recetas, embalajes y costes.',
    searchMaterials: 'Buscar código o nombre',
    newMaterial: 'Nuevo material',
    materialCode: 'Código de material',
    materialName: 'Nombre de material',
    materialCodeHelp: 'Código interno / ERP / proveedor, p. ej. 51465',
    materialNameHelp: 'Nombre utilizado en recetas, p. ej. Nauta Hakkefni',
    savingMaterial: 'GUARDANDO...',
    materialCreateSuccess: 'Material añadido.',
    materialCreateErrors: {
      duplicateCode: 'Ya existe un material con el código {{code}}.',
      invalidCategoryOrUnit: 'Elige un tipo de material y una unidad válidos.',
      permissionDenied: 'No tienes permiso para añadir materiales.',
      validationFailed: 'Revisa los campos obligatorios y los datos.',
      saveFailed: 'No se pudo guardar el material. Inténtalo de nuevo.',
    },
    allMaterials: 'Todos',
    noMaterials: 'Ningún material coincide con el filtro.',
    usedBy: 'Usado en',
    active: 'Activo',
    archived: 'Archivado',
    yes: 'Sí',
    no: 'No',
    close: 'Cerrar',
    save: 'Guardar',
    costManaged: 'Incluir en el cálculo de coste',
    trackStock: 'Controlar existencias de almacén',
    includeCost: 'Incluir en el cálculo de coste',
    materialNoCostHelp:
      'Un material sin gestión de coste no necesita precio y no vuelve incompleto el cálculo hasta que se usa como entrada de coste.',
    rollLengthHelp: 'Longitud de un rollo en metros. El precio del rollo se normaliza por metro.',
    noCostHistory: 'No hay historial de coste guardado.',
    loadFailed: 'No se pudieron cargar los materiales.',
    materialCategory: {
      RAW_MATERIAL: 'Materia prima',
      ADDITIVE: 'Aditivo',
      PACKAGING: 'Embalaje',
      FILM: 'Film',
      UNCLASSIFIED: 'Sin clasificar',
    },
    productionCostWorkspace: 'Coste de producción',
    productionCostWorkspaceDescription:
      'Economía completa del lote desde materias primas hasta margen. Cada versión conserva su instantánea de precios.',
    calculator: 'Calculadora',
    calculationHeader: 'Cabecera del cálculo',
    calculationHeaderHelp: 'Identidad, base del lote y contexto comercial de esta versión.',
    standardCalculation: 'Estándar',
    researchCalculation: 'I+D',
    finishedProduct: 'Producto terminado',
    recipe: 'Receta',
    recipeVersion: 'Receta v{{version}}',
    noActiveRecipe: 'Sin receta activa',
    calculationName: 'Nombre del cálculo',
    newCalculation: 'Nuevo cálculo',
    variant: 'Variante del producto',
    inputBatchKg: 'Lote de entrada (kg)',
    pieceWeightGrams: 'Peso por pieza (g)',
    piecesPerCarton: 'Piezas por caja',
    customerOrNetwork: 'Cliente / red',
    noSpecificCustomer: 'Sin cliente específico',
    searchProduct: 'Buscar por código, nombre o alias',
    searchMaterial: 'Buscar por código o nombre',
    recentlyUsed: 'usado recientemente',
    activeRecipe: 'receta activa',
    openProductCard: 'Abrir ficha del producto',
    noCurrentPrice: 'Sin precio actual',
    historicalPrice: 'Precio histórico',
    priceEffectiveOn: 'Precio vigente {{date}}',
    abpPrice: 'Precio ABP',
    abpPriceEffectiveOn: 'ABP · {{date}}',
    catalogGroup: {
      rawMaterials: 'Materias primas locales',
      additives: 'Aditivos',
      packaging: 'Embalaje',
      abp: 'Materias primas ABP',
      manual: 'Entrada manual',
      networks: 'Redes',
      stores: 'Tiendas',
    },
    note: 'Nota',
    additionalSettings: 'Ajustes adicionales',
    additionalSettingsHelp: 'Nombre, variante, peso por pieza y nota opcionales.',
    loadCanonicalRecipe: 'Cargar receta canónica actual',
    recipeCalculationHelp: 'Introduce kilogramos o porcentajes exactos.',
    chooseProductOrAddIngredient: 'Elige un producto o añade el primer ingrediente',
    missingCostData: 'Faltan datos de coste',
    customerPrice: 'Precio para el cliente',
    recipePercent: 'Receta en porcentajes',
    recipeKg: 'Receta en kilogramos',
    manualEntry: 'Partida manual del escenario',
    manualOverride: 'Valor manual',
    lossAndYield: 'Pérdidas y rendimiento',
    lossAndYieldHelp: 'Las pérdidas se aplican secuencialmente y nunca dos veces.',
    addLossStage: 'Añadir etapa de pérdida',
    lossMode: 'Unidad de pérdida',
    lossValue: 'Pérdida',
    massAfterStage: 'Masa tras la etapa',
    laborOperations: 'Operaciones y mano de obra',
    laborOperationsHelp: 'Personas × horas × coste horario completo.',
    addOperation: 'Añadir operación',
    operation: 'Operación',
    hours: 'Horas',
    hourlyLoadedCost: 'Coste horario completo',
    machinesAndEnergy: 'Máquinas y energía',
    machinesAndEnergyHelp: 'El tiempo de máquina se mantiene separado de la mano de obra.',
    addMachine: 'Añadir máquina',
    hourlyCost: 'Coste por hora',
    packagingMaterials: 'Materiales de embalaje',
    packagingMaterialsHelp: 'Consumo por pieza, caja o lote, incluido el desperdicio.',
    calculationBasis: 'Base de cálculo',
    consumption: 'Consumo',
    wastePercent: 'Desperdicio (%)',
    perPiece: 'Por pieza',
    perCarton: 'Por caja',
    perBatch: 'Por lote',
    explicitOtherCosts: 'Otros costes explícitos',
    explicitOtherCostsHelp:
      'Calidad, limpieza, almacén, logística, energía y residuos quedan visibles.',
    addExplicitCost: 'Añadir coste explícito',
    category: 'Categoría',
    name: 'Nombre',
    rate: 'Tarifa',
    otherCategory: {
      QUALITY_CONTROL: 'Control de calidad',
      CLEANING_SANITATION: 'Limpieza y desinfección',
      STORAGE: 'Almacenamiento',
      INTERNAL_LOGISTICS: 'Logística interna',
      ADDITIONAL_ENERGY: 'Energía adicional',
      WASTE_DISPOSAL: 'Gestión de residuos',
    },
    costBasis: {
      PER_BATCH: 'Por lote',
      PER_KG: 'Por kg',
      PER_HOUR: 'Por hora',
      PER_PALLET: 'Por palé',
    },
    netPriceHelp: 'Todos los precios de venta y márgenes son netos.',
    priceUnitLabel: 'Unidad del precio de venta',
    piece: 'pieza',
    carton: 'caja',
    distributionCost: 'Coste de distribución / lote',
    discountPercent: 'Descuento (%)',
    finishedMass: 'Masa terminada',
    yield: 'Rendimiento',
    fullPieces: 'Piezas completas',
    fullCartons: 'Cajas completas',
    costPerKg: 'Coste / kg',
    costPerPiece: 'Coste / pieza',
    costPerCarton: 'Coste / caja',
    unsavedChanges: 'Cambios sin guardar',
    discardUnsavedChanges: '¿Descartar los cambios no guardados?',
    archiveCalculationConfirm: '¿Archivar este cálculo? Se conservará el historial inmutable.',
    savingCalculation: 'GUARDANDO…',
    calculationSaved: 'Versión del cálculo guardada.',
    calculating: 'Calculando…',
    fixValidationErrors: 'Corrige los errores antes de guardar.',
    missingUsedCosts: '{{count}} entradas utilizadas no tienen precio autorizado.',
    completeness: {
      COMPLETE: 'Completo',
      PARTIAL: 'Parcial',
      UNKNOWN_COST: 'Coste desconocido',
    },
    validation: {
      INPUT_BATCH_MUST_BE_POSITIVE: 'El lote de entrada debe ser mayor que cero.',
      RECIPE_PERCENT_MUST_EQUAL_100: 'Los porcentajes de la receta deben sumar exactamente 100%.',
      LOSS_PERCENT_EXCEEDS_100: 'La pérdida porcentual no puede superar el 100%.',
      LOSS_EXCEEDS_AVAILABLE_MASS: 'La pérdida no puede superar la masa disponible.',
      PIECE_WEIGHT_MUST_BE_POSITIVE: 'El peso por pieza debe ser mayor que cero.',
      PACKAGING_CONVERSION_REQUIRED: 'El embalaje por pieza/caja requiere conversión explícita.',
      SELLING_UNIT_CONVERSION_REQUIRED: 'La unidad de venta requiere conversión explícita.',
      DISCOUNT_EXCEEDS_100: 'El descuento no puede superar el 100%.',
      TARGET_MARGIN_MUST_BE_BELOW_100: 'El margen objetivo debe ser inferior al 100%.',
      VALUE_MUST_BE_NON_NEGATIVE: 'El valor no puede ser negativo.',
    },
    recipePercentTotal: 'Total de receta',
    all: 'Todos',
    approved: 'Aprobado',
    sort: 'Ordenar',
    sortUpdated: 'Actualizados recientemente',
    sortCalculationDate: 'Fecha de cálculo (más reciente)',
    sortName: 'Nombre',
    sortCostAsc: 'Coste unitario ascendente',
    sortCostDesc: 'Coste unitario descendente',
    sortMarginAsc: 'Margen ascendente',
    sortMarginDesc: 'Margen descendente',
    dateFrom: 'Fecha de cálculo desde',
    dateTo: 'Fecha de cálculo hasta',
    comparisonDifference: 'Diferencia',
    comparisonDifferencePercent: 'Diferencia %',
    laborTime: 'Tiempo de trabajo',
    defaultApprovalReason: 'Instantánea aprobada del coste de producción',
    departments: 'departamentos',
    calculations: 'cálculos',
    costBaseTruthHelp:
      'Materiales y tarifas efectivas se comparten con Producción; esta pantalla no crea otro maestro.',
    openMaterials: 'Abrir maestro de materiales',
    version: 'Versión',
    compareVersions: 'Comparar versiones guardadas',
    scenarioA: 'Escenario A',
    scenarioB: 'Escenario B',
    metric: 'Métrica',
    restore: 'Restaurar',
  },
  orders: {
    title: 'Pedidos de tiendas',
    description: 'Demanda explícita de tienda por ronda de entrega.',
    entry: 'INTRODUCIR PEDIDOS',
    summary: 'RESUMEN',
    submitted: 'PEDIDOS ENVIADOS',
    newRound: 'Nueva ronda',
    closeRound: 'Cerrar ronda',
    round: 'Ronda de entrega',
    deliveryDate: 'Fecha de entrega',
    reference: 'Referencia',
    expectedStores: 'Tiendas previstas',
    storesInOrder: 'Tiendas del pedido',
    addStore: 'Añadir tienda',
    addStoreButton: 'Añadir',
    addingStore: 'AÑADIENDO...',
    removeStore: 'Eliminar tienda',
    removingStore: 'ELIMINANDO...',
    removeStoreConfirm: '¿Eliminar {{store}} de esta ronda?',
    removeDraftStoreConfirm:
      'Esta tienda tiene un borrador guardado. ¿Cancelar el borrador actual y eliminar la tienda de la ronda?',
    cancelDraftAndRemove: 'Cancelar borrador y eliminar',
    confirmedStoreRemoveBlocked:
      'Esta tienda tiene un pedido confirmado. Cancele primero el pedido.',
    removeConfirmedStoreConfirm:
      '¿Cancelar el pedido confirmado de {{store}} y eliminar la tienda de esta ronda?',
    cancelConfirmedOrderAndRemove: 'Cancelar pedido y eliminar tienda',
    confirmedOrderCancelledAndStoreRemoved: 'Pedido cancelado. Tienda eliminada de la ronda.',
    roundUpdateErrors: {
      staleRevision: 'La ronda cambió. Los datos se actualizaron; inténtelo de nuevo.',
      invalidStore: 'No se puede añadir la tienda seleccionada.',
      roundNotEditable: 'No se puede modificar una ronda cerrada.',
      activeStoreOrders: 'No se puede eliminar de la ronda una tienda con un pedido activo.',
      confirmedStoreLocked: 'Esta tienda tiene un pedido confirmado. Cancele primero el pedido.',
      addFailed: 'No se pudo añadir la tienda.',
      removeFailed: 'No se pudo eliminar la tienda.',
    },
    searchStore: 'Buscar tienda',
    selectStore: 'Seleccione una tienda para introducir el pedido.',
    noRound: 'No hay una ronda de pedidos abierta.',
    noRoundForWeek:
      'No hay una ronda de pedidos en esta semana. Las semanas históricas cerradas siguen disponibles al seleccionarlas.',
    weekHeading: 'SEMANA {{week}} · {{from}}–{{to}}',
    previousWeek: 'Semana anterior',
    currentWeek: 'Semana actual',
    nextWeek: 'Semana siguiente',
    department: 'Departamento',
    allDepartments: 'Todos los departamentos',
    network: 'Red',
    allNetworks: 'Todas las redes',
    addProduct: 'Añadir producto',
    removeProduct: 'Eliminar producto',
    orderEditor: 'Editor de pedido',
    quantity: 'Cantidad',
    note: 'Nota',
    saveDraft: 'Guardar borrador',
    confirm: 'Confirmar pedido',
    confirmedStores: 'Tiendas confirmadas',
    missingStores: 'Tiendas pendientes',
    product: 'Producto',
    ordered: 'Pedido',
    planned: 'Ya planificado',
    remaining: 'POR HACER',
    statusLabel: 'Estado',
    productionWarehouse: 'Almacén de producción',
    createProduction: 'Crear borrador de producción',
    ready: 'Listo',
    productionConfigured: 'Producción configurada',
    pickingRequired: 'Para preparar',
    overplanned: 'Sobreplanificado',
    resetDepartmentOrder: 'Restablecer orden de departamentos',
    resetProductOrder: 'Restablecer orden de productos',
    storesList: 'Lista de tiendas',
    productMatrix: 'Matriz de productos',
    store: 'Tienda',
    version: 'Versión',
    confirmedAt: 'Confirmado',
    show: 'Mostrar',
    history: 'Historial de versiones',
    previous: 'Tienda anterior',
    next: 'Siguiente tienda',
    create: 'Crear',
    cancel: 'Cancelar',
    loading: 'Cargando…',
    notReceived: 'No recibido',
    importOrderFile: 'Importar archivo de pedido',
    fileImportReview: 'Revisión de importación de archivo',
    pdfTextUnavailable: 'Este PDF no contiene texto para la importación automática.',
    storeMatches: 'TIENDA COINCIDE',
    possibleStoreMismatch: 'POSIBLE DISCREPANCIA DE TIENDA: se detectó {{store}}.',
    fileImportFailed: 'No se pudo leer el archivo. Puede introducir el pedido manualmente.',
    selectedStore: 'Tienda seleccionada: {{store}}',
    readFromImage: 'Leer desde imagen',
    imageReview: 'Revisión de lectura de imagen',
    imageReadFailed: 'No se pudo leer la imagen. Puede introducir el pedido manualmente.',
    retryImageRead: 'Intentar de nuevo',
    enterManually: 'Introducir manualmente',
    reviewRequired: 'Requiere revisión',
    addToDraft: 'Añadir al borrador del pedido',
    sources: 'Fuentes',
    removeLine: 'Eliminar línea',
    rememberImageAlias: 'Recordar alias',
    imageState: {
      READY: 'LISTO',
      REVIEW: 'REVISIÓN',
      UNMATCHED: 'SIN COINCIDENCIA',
      INVALID_QUANTITY: 'CANTIDAD INVÁLIDA',
      UNIT_REVIEW: 'REVISAR UNIDAD',
    },
    status: {
      NOT_RECEIVED: 'NO RECIBIDO',
      DRAFT: 'BORRADOR',
      CONFIRMED: 'CONFIRMADO',
    },
  },
  abpCatalog: {
    title: 'Catálogo de productos ABP',
    subtitle: 'Productos ABP de solo lectura, precios actuales y último coste final fiable.',
    syncNow: 'Sincronizar ahora',
    lastSync: 'Última sincronización correcta: {{date}}',
    neverSynced: 'Nunca sincronizado',
    productCount: '{{count}} productos',
    syncError:
      'La última sincronización falló ({{code}}). El último catálogo válido sigue disponible.',
    notConfigured:
      'La sincronización ABP no está configurada. La caché existente sigue siendo de solo lectura.',
    manualSyncFailed: 'La sincronización manual falló. No se eliminó ningún producto almacenado.',
    search: 'Nombre, alias o PLU',
    freshFrozen: 'Fresco / congelado',
    all: 'Todos',
    family: 'Familia',
    visibility: 'Visibilidad',
    priceSource: 'Origen del precio',
    dataCompleteness: 'Datos ausentes',
    sort: 'Ordenar',
    product: 'Producto',
    plu: 'PLU',
    familyVariant: 'Familia / variante',
    priceEur: 'ABP EUR/kg',
    finalCost: 'Final ISK/kg',
    dataStatus: 'Estado de datos',
    details: 'Detalles',
    noResults: 'Ningún producto coincide con los filtros.',
    loadFailed: 'No se pudo cargar el catálogo ABP.',
    officialName: 'Nombre oficial',
    abpId: 'Identificador ABP',
    hsCode: 'Código HS',
    importMode: 'Método de importación',
    costBasis: 'Base del coste',
    warnings: 'Avisos',
    none: 'Ninguno',
    close: 'Cerrar',
    sync: {
      current: 'Actual',
      stale: 'Desactualizado',
      error: 'Error de sincronización',
      not_configured: 'Sin configurar',
      empty: 'Caché vacía',
      syncing: 'Sincronizando',
    },
    source: {
      pricebook: 'Lista activa',
      invoiceFallback: 'Última factura alternativa',
      missing: 'Sin precio',
    },
    status: {
      available: 'Disponible',
      unavailable: 'No disponible',
      hidden: 'Oculto',
      review: 'Revisar',
      complete: 'Completo',
    },
    visibilityOption: {
      all: 'Todos',
      active: 'Activos',
      hidden: 'Ocultos',
      unavailable: 'No disponibles',
    },
    missingOption: {
      any: 'Todos',
      price: 'Sin precio',
      cost: 'Sin coste',
      none: 'Precio y coste completos',
    },
    sortOption: {
      name: 'Nombre',
      plu: 'PLU',
      price_eur: 'Precio EUR',
      cost_isk: 'Coste ISK',
      updated_at: 'Actualización',
    },
    direction: { asc: 'Ascendente', desc: 'Descendente' },
    import: { tariff: 'Arancel', quota: 'Cuota', exempt: 'Exento / 0%' },
    review: {
      missing_price: 'falta precio',
      ambiguous_price_source: 'origen de precio ambiguo',
      missing_family: 'falta familia',
      missing_variant: 'falta variante',
      missing_fresh_frozen: 'falta fresco/congelado',
      missing_hs_code: 'falta código HS',
      missing_import_mode: 'falta método de importación',
      missing_final_cost: 'falta coste final',
    },
  },
  profitability: {
    title: 'Rentabilidad',
    subtitle: 'Vista de solo lectura de ventas netas confirmadas y costes históricos efectivos.',
    readOnly: 'Solo lectura',
    from: 'Desde',
    to: 'Hasta',
    all: 'Todos',
    product: 'Producto',
    store: 'Tienda',
    network: 'Red',
    products: 'Productos',
    stores: 'Tiendas',
    networks: 'Redes',
    view: 'Vista',
    sort: 'Ordenar',
    direction: 'Dirección',
    descending: 'Descendente',
    ascending: 'Ascendente',
    quantity: 'Cantidad',
    revenue: 'Ingresos netos',
    cost: 'Coste',
    profit: 'Beneficio',
    margin: 'Margen',
    completeness: 'Integridad del coste',
    date: 'Fecha',
    overTime: 'Rentabilidad a lo largo del tiempo',
    noRows: 'No hay ventas confirmadas que coincidan con los filtros.',
    loadFailed: 'No se pudo cargar la rentabilidad.',
    costProvenance: 'Procedencia del coste',
    standardOnly:
      'Se utiliza el coste estándar efectivo. Nunca se adivina el coste real de lote sin una relación autorizada venta-lote.',
    costVersionCount_one: '{{count}} versión de coste estándar',
    costVersionCount_other: '{{count}} versiones de coste estándar',
    state: {
      COMPLETE: 'Completo',
      PARTIAL: 'Parcial',
      UNKNOWN_COST: 'Coste desconocido',
    },
    basis: {
      STANDARD_EFFECTIVE: 'Coste estándar efectivo',
      NO_AUTHORITATIVE_COST: 'Sin coste autorizado',
    },
  },
} satisfies LocaleResources;
